// Settings menu wiring: syncs the menu controls with the Settings object and
// applies changes to the game + localStorage.

import {
  Settings,
  type SettingsStore,
  encodeConfig,
  decodeConfig,
  DEFAULTS,
  DOWNSTREAM_RATES,
  type HudSettings,
  type TrailColorMode,
  type TrailConfig,
} from "../settings";
import type { HudLayoutStore } from "../hud/types";
import { canRole, loadAdminToken, type AdminSettings, type Role } from "../admin";
import { fetchLeaderboard, fetchProfile, getIdentity, registerNickname } from "../net/leaderboard";
import { CrosshairEditor } from "./crosshair_editor";
import { AdminPanel } from "./admin_panel";
import { CgazStylePanel } from "./cgaz_style";
import { HudEditor, resetHudStore } from "./hud_editor";
import { WeaponSettingsPanel } from "./weapon_settings";
import { dsElement, refreshComponentStyles } from "./daisy";
import { canSeeSetting } from "../settings/access";
import {
  accessFor,
  applyGlobalDefaults,
  atDefault,
  clearAllExplicit,
  clearExplicit,
  effectiveValue,
  loadCachedGlobalDefaults,
  markExplicit,
  resetSetting,
} from "../settings/defaults";
import { SETTINGS, formatDefault, pageForSetting, type SettingDef } from "../settings/registry";
import { searchSettings } from "../settings/search";
import { SettingsSearch } from "./settings_search";
import type { CrosshairStore } from "../crosshair/types";
import type { WeaponSettings } from "../weapons/settings";
import { ticksToMs } from "../tickrate";
import type { RosterEntry } from "../net/transport";
import {
  ACTION_GROUPS,
  ACTION_LABELS,
  DEFAULT_BINDS,
  ESSENTIAL_ACTION_GROUPS,
  PRACTICE_ACTIONS,
  BindMap,
  displayCode,
} from "../binds";
import { SOUND_EVENTS } from "../audio";
import type { TextureMode } from "../render/renderer";
import {
  MapInfo,
  getCatalog,
  searchMaps,
  parseVote,
  resolveVote,
  getFavorites,
  toggleFavorite,
  isFavorite,
  TESTING_MAPS,
  getMapPacks,
  type MapPackInfo,
} from "../maps";
import { presetForMap } from "../maps";
import { api } from "../base";
import { q3Html } from "../net/q3colors";
import {
  RULE_GROUPS,
  RuleField,
  RuleGroup,
  clampRule,
  loadProfiles,
  saveProfiles,
  ruleDefaults,
  PRESETS,
  presetValues,
  presetNote,
  presetLabel,
  DASH_ON,
  DASH_OFF,
  WJ_ON,
  WJ_OFF,
  dashEnabled,
  walljumpEnabled,
  hashRules,
} from "../rules";
import {
  GameMode,
  MODE_GROUPS,
  MODE_PRESETS,
  ModeField,
  clampModeField,
  hashMode,
  loadModeProfiles,
  modePresetId,
  modeLabel,
  presetMode,
  saveModeProfiles,
} from "../modes";

function getNickname(): string | null {
  const id = getIdentity();
  return id ? id.nickname : null;
}

export interface RecallInfo {
  /** 0 = Run, 1 = Practice (basic), 2 = Noclip, 3 = Recall. */
  mode: number;
  /** Number of keyframes in the recording. */
  count: number;
  /** Selected keyframe index. */
  index: number;
  /** Tick of the selected keyframe. */
  tick: number;
  /** Last recorded tick (context). */
  length: number;
  /** Keyframe ticks. */
  keyframes: number[];
}

/** Which settings page a dynamically-rendered tuning slider belongs to. */
type TuningGroup = "video-effects" | "video-performance" | "network-tuning" | "network-test";

/** Tuning sliders (mirror the URL options), grouped by their settings page. */
interface TuningSlider {
  key: string;
  group: TuningGroup;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  /** Hover tooltip explaining the knob. */
  tip?: string;
}

const TUNING_SLIDERS: TuningSlider[] = [
  // Network — tuning.
  { key: "interp", group: "network-tuning", label: "Interp delay", min: 20, max: 300, step: 1, unit: "ms",
    tip: "How far in the past remote players are rendered (ms). Lower = closer to present, but more jitter." },
  { key: "smooth", group: "network-tuning", label: "Correction smoothing", min: 0, max: 1000, step: 10, unit: "ms",
    tip: "How long server position corrections are smoothed over (ms). Higher = softer, laggier correction." },
  // Network — impairment simulator.
  { key: "lag", group: "network-test", label: "Sim latency (one-way)", min: 0, max: 1000, step: 5, unit: "ms",
    tip: "Simulated one-way network latency (ms) — for testing netcode." },
  { key: "jitter", group: "network-test", label: "Sim jitter", min: 0, max: 500, step: 5, unit: "ms",
    tip: "Simulated ± packet jitter (ms) — for testing netcode." },
  { key: "loss", group: "network-test", label: "Sim loss", min: 0, max: 100, step: 1, unit: "%",
    tip: "Simulated packet loss (%) — for testing netcode." },
  // Video — performance.
  { key: "fpscap", group: "video-performance", label: "FPS cap (0 = off)", min: 0, max: 500, step: 1, unit: "fps",
    tip: "0 = uncapped/vsync (cleanest pacing). A software cap runs off-vsync and can tear — pick a multiple of the display rate (e.g. 360 on a 360 Hz panel). 333/360 work via the high-resolution scheduler." },
  // Video — effects.
  { key: "vfxamount", group: "video-effects", label: "VFX amount", min: 0, max: 3, step: 0.1, unit: "×",
    tip: "Multiplier for how many glow particles spawn." },
  { key: "smokedensity", group: "video-effects", label: "Smoke density", min: 0, max: 3, step: 0.1, unit: "×",
    tip: "Multiplier for smoke particle count." },
  { key: "smokeopacity", group: "video-effects", label: "Smoke opacity", min: 0, max: 1, step: 0.05, unit: "",
    tip: "How opaque the smoke is (0 = invisible)." },
  { key: "vfxsize", group: "video-effects", label: "VFX size", min: 0.2, max: 3, step: 0.1, unit: "×",
    tip: "Particle size multiplier." },
  { key: "bloomintensity", group: "video-effects", label: "Bloom intensity", min: 0, max: 3, step: 0.05, unit: "",
    tip: "How strong the bloom glow is." },
  { key: "bloomthreshold", group: "video-effects", label: "Bloom threshold", min: 0, max: 1, step: 0.05, unit: "",
    tip: "Brightness above which pixels bloom. Lower = more glow." },
];

/** Settings pages that share a sub-navigation group. */
const NAV_GROUPS: Record<string, string> = {
  settings: "settings",
  video: "settings",
  hud: "settings",
  audio: "settings",
  controls: "settings",
  weapons: "settings",
  modes: "settings",
  rules: "settings",
  advanced: "settings",
  maps: "maps",
  favorites: "maps",
  packs: "maps",
  leaderboards: "maps",
};

function formatDebug(d: TuningSlider, v: number): string {
  return `${d.step < 1 ? v.toFixed(2) : String(v)}${d.unit ? " " + d.unit : ""}`;
}

/** Read a `data-fb` control's value in its native type. */
function feedbackValue(el: HTMLInputElement | HTMLSelectElement): boolean | number | string {
  if (el instanceof HTMLInputElement) {
    if (el.type === "checkbox") return el.checked;
    if (el.type === "range" || el.type === "number") return Number(el.value);
  }
  return el.value;
}

/** Human-readable value for a `data-fb` control's `-value` span. */
function feedbackText(id: string, v: unknown): string {
  const n = Number(v);
  if (id.endsWith("-duration")) return `${n} ms`;
  if (id.endsWith("-size") || id.endsWith("-thickness")) return `${n} px`;
  if (id.endsWith("-brightness")) return `${n}×`;
  if (id.endsWith("-volume")) return `${n}×`;
  if (id.endsWith("-opacity")) return `${Math.round(n * 100)}%`;
  return String(v);
}

/** Weapon key prefix (rules group id) -> weapon id. */
const WEAPON_ID: Record<string, number> = {
  melee: 1, machinegun: 2, shotgun: 3, grenade: 4,
  rocket: 5, plasma: 6, lightning: 7, railgun: 8, experimental: 9, ball: 10,
};

export interface SettingsCallbacks {
  onFov: (fov: number) => void;
  onPanini: (strength: number) => void;
  onSensitivity: (sens: number) => void;
  onBinds: (binds: BindMap) => void;
  /** Return to the landing page (home). */
  onHome: () => void;
  onPlayMap: (map: string) => void;
  /** Run a console command line (used by the settings search's vote actions). */
  onConsoleCommand: (line: string) => void;
  /** Current server family (race/freestyle vs combat); filters mode votes. */
  getServerFamily: () => "race" | "combat" | null;
  /** Texture filtering quality changed. */
  onTextureMode: (mode: TextureMode) => void;
  /** The stored nickname changed (sync the landing field + live server). */
  onNickname: (nickname: string) => void;
  /** A single dev/tuning rule changed (send to the connected server). */
  onRule: (key: string, value: number) => void;
  /** The whole rule set changed (profile load / reset; push all at once). */
  onRules: (rules: Record<string, number>) => void;
  /** Apply rules to the local prediction only (adopting the server's rules). */
  onRulesLocal: (rules: Record<string, number>) => void;
  /** Request the server's authoritative ruleset. */
  onRequestRules: () => void;
  /** Propose the current rules as a vote. */
  onVoteRules: (rules: Record<string, number>, label: string) => void;
  /** Apply a game mode locally (adopting the server's mode / local preset). */
  onGameModeLocal: (mode: GameMode) => void;
  /** Propose the current mode package (mode + rules) as a vote. */
  onVoteGameMode: (mode: GameMode, rules: Record<string, number>, label: string) => void;
  /** Request the server's authoritative mode. */
  onRequestGameMode: () => void;
  /** Sound assignment (event id -> file name) changed. */
  onSounds: (sounds: Record<string, string>) => void;
  /** Master sound volume changed (0..1). */
  onVolume: (volume: number) => void;
  /** `cg_truelightning` changed (0..1): LG beam anchored to the crosshair. */
  onTrueLightning: (value: number) => void;
  /** Player-trail appearance changed (persisted). */
  onTrail: (config: TrailConfig) => void;
  onFeedback?: () => void;
  /** Global admin defaults (server-backed). */
  getAdminSettings: () => AdminSettings;
  onAdminSettings: (settings: AdminSettings) => void;
  onSaveAdminSettings: (
    settings: AdminSettings,
    token: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Current (preview) role; accounts land later. */
  getRole: () => Role;
  onRole: (role: Role) => void;
  /** The layered crosshair store changed (slots/active config). */
  onCrosshair: (store: CrosshairStore) => void;
  /** Per-weapon settings changed. */
  onWeaponSettings: (settings: WeaponSettings) => void;
  /** Movement HUD aid visibility changed. */
  onHud: (hud: HudSettings) => void;
  /** HUD layout / value colours changed. `preview` = doc being edited. */
  onHudLayout: (store: HudLayoutStore, preview?: import("../hud/types").HudDocument) => void;
  /** The full-screen HUD editor opened/closed. */
  onHudEditor: (open: boolean) => void;
  /** The command palette opened/closed (used for its wireframe preview). */
  onPaletteToggle: (open: boolean) => void;
  /** Live value for the editor's colour previews. */
  onHudTestValue: (source: "health" | "armor" | "ammo" | "speed", value: number) => void;
  /** Live client-debug/network state (mirrors the URL options). */
  getDebug: () => Record<string, number | boolean>;
  /** Raw pointer status for the Weapons → Mouse row. */
  getRawStatus: () => string;
  /** Apply one live video/network/advanced option. */
  onDebug: (key: string, value: number | boolean) => void;
  /** Restore every live video/network/advanced option to its default. */
  onDebugReset: () => void;
  /** Reconnect/reload with the given `server`/`map` URL options. */
  onDebugReload: (server: string, map: string) => void;
  /** Get/set a VFX colour (hex) live (Video tab). */
  getVfxColor: (key: string) => string;
  onVfxColor: (key: string, hex: string) => void;
  /** Get/set a weapon's projectile/pellet colour (hex) live (Rules tab). */
  getProjectileColor: (weapon: number) => string;
  onProjectileColor: (weapon: number, hex: string) => void;
  /** Get/set a weapon's projectile/orb opacity (0..1) live (Rules tab). */
  getProjectileAlpha: (weapon: number) => number;
  onProjectileAlpha: (weapon: number, alpha: number) => void;
  /** Preview a specific `snd/` file. */
  onPlaySound: (file: string) => void;
  /** Fetch the live list of files available under `snd/`. */
  onListSounds: () => Promise<string[]>;
  /** Set the game mode (0 Run, 1 Practice, 2 Noclip, 3 Recall). */
  onMode: (mode: number) => void;
  /** Step the recall keyframe selection by `delta`. */
  onRecallStep: (delta: number) => void;
  /** Select a recall keyframe index. */
  onRecallSelect: (index: number) => void;
  /** Restart (starts a run from the selected keyframe when in recall). */
  onRestart: () => void;
  /** Current recall / mode info, or null when there is no session. */
  getRecall: () => RecallInfo | null;
  /** Save the current full state under a named slot. */
  onSaveSlot: (name: string) => void;
  /** Teleport to a named slot. */
  onRecallSlot: (name: string) => void;
  /** Delete a named slot. */
  onDeleteSlot: (name: string) => void;
  /** Named slot names (insertion order). */
  getSlots: () => string[];
  /** Current ghost source ("last" | "pr" | "wr"). */
  getGhostSource: () => string;
  /** Change the ghost source. */
  onGhostSource: (source: string) => void;
  /** Playtest team switch: move a roster slot to team 0/1/2. */
  onTeam: (slot: number, team: number) => void;
  /** Live connected-player roster (nickname / ping / session best). */
  getRoster: () => RosterEntry[];
  /** The current map name (for the Players tab's PB lookup). */
  getCurrentMap: () => string;
}

/** Minimal MapInfo for a name the catalog doesn't know (still launchable). */
function stubMapInfo(name: string, extra?: Partial<MapInfo>): MapInfo {
  return {
    map_name: name,
    normalized_name: name,
    game: "",
    longname: null,
    author: null,
    has_rocket_launcher: 0,
    has_plasmagun: 0,
    has_grenade_launcher: 0,
    has_slick: 0,
    has_water: 0,
    has_slime: 0,
    has_lava: 0,
    has_fog: 0,
    has_teleporter: 0,
    has_jumppad: 0,
    has_timer: 0,
    record_count: 0,
    best_time_ms: null,
    best_player_name: null,
    ...extra,
  };
}

/**
 * Static markup selectors for registered settings.
 *
 * Dynamic controls use `data-setting="<id>"` directly. This table is the bridge
 * for the existing `index.html` controls and can shrink as markup is migrated.
 */
/** Containers hidden when a structured editor group is not visible. */
const GROUP_PAGES: Record<string, string> = {
  "controls.binds": "controls",
  "weapons.settings": "weapons",
  "weapons.sounds": "audio",
  "hud.layout": "hud",
  "crosshair.store": "hud",
};

const GROUP_CONTAINERS: Record<string, string[]> = {
  "controls.binds": ["#binds"],
  "weapons.settings": ["#weapon-settings"],
  "weapons.sounds": ["#sound-list"],
  "hud.layout": ["#open-hud-editor"],
  "crosshair.store": ["#crosshair-editor"],
};

const SETTING_CONTROLS: Record<string, string[]> = {
  "video.fov": ["#fov-slider", "#fov-input"],
  "video.panini": ["#panini-slider", "#panini-input"],
  "video.textureMode": ["#texture-mode"],
  "video.effects.truelightning": ["#truelightning-slider"],
  "video.effects.trailSeconds": ["#trail-slider"],
  "video.effects.trailWidth": ["#trail-width"],
  "video.effects.trailIntensity": ["#trail-glow"],
  "video.effects.trailSelfMode": ["#trail-self-mode"],
  "video.effects.trailSelfColor": ["#trail-self-color"],
  "video.effects.trailSelfTeamColor": ["#trail-self-team-color"],
  "video.effects.trailEnemyMode": ["#trail-enemy-mode"],
  "video.effects.trailEnemyColor": ["#trail-enemy-color"],
  "video.effects.trailEnemyTeamColor": ["#trail-enemy-team-color"],
  "video.enemy.outlineSize": ["#enemy-outline-size"],
  "video.enemy.outlineBrightness": ["#enemy-outline-brightness"],
  "video.enemy.haloSize": ["#enemy-halo-size"],
  "video.enemy.haloBrightness": ["#enemy-halo-brightness"],
  "video.vfx.explosion": ['[data-vfx-color="explosion"]'],
  "video.vfx.sparks": ['[data-vfx-color="sparks"]'],
  "video.vfx.muzzle": ['[data-vfx-color="muzzle"]'],
  "video.vfx.smoke": ['[data-vfx-color="smoke"]'],
  "video.hit.hitmarker": ["#hitmarker-on"],
  "video.hit.hitmarkerStyle": ["#hitmarker-style"],
  "video.hit.hitmarkerSize": ["#hitmarker-size"],
  "video.hit.hitmarkerThickness": ["#hitmarker-thickness"],
  "video.hit.hitmarkerColor": ["#hitmarker-color"],
  "video.hit.hitmarkerKillColor": ["#hitmarker-kill-color"],
  "video.hit.hitmarkerDuration": ["#hitmarker-duration"],
  "video.hit.hitmarkerOpacity": ["#hitmarker-opacity"],
  "video.hit.damageNumbers": ["#damage-numbers-on"],
  "video.hit.damageNumberSize": ["#damage-number-size"],
  "video.hit.damageNumberColor": ["#damage-number-color"],
  "video.hit.damageNumberKillColor": ["#damage-number-kill-color"],
  "video.hit.damageNumberDuration": ["#damage-number-duration"],
  "video.hit.damageNumberRise": ["#damage-number-rise"],
  "video.hit.damageNumberOpacity": ["#damage-number-opacity"],
  "audio.hitSoundVolume": ["#hit-sound-volume"],
  "audio.volume": ["#volume-slider"],
  "advanced.downstreamHz": ["#dbg-downstream"],
  "advanced.debug.wireframe": ['[data-dbg-toggle="wireframe"]'],
  "advanced.debug.pvs": ['[data-dbg-toggle="pvs"]'],
  "advanced.debug.frustum": ['[data-dbg-toggle="frustum"]'],
  "advanced.debug.rawmouse": ['[data-dbg-toggle="rawmouse"]'],
  "advanced.debug.vfxtrails": ['[data-dbg-toggle="vfxtrails"]'],
  "advanced.debug.vfxcollide": ['[data-dbg-toggle="vfxcollide"]'],
  "advanced.debug.bloom": ['[data-dbg-toggle="bloom"]'],
};

export class Menu {
  private readonly store: SettingsStore;
  private settings: Settings;
  private menuEl: HTMLElement;
  private settingsSearchInput: HTMLInputElement;
  private settingsSearchHint: HTMLElement;
  private settingsSearchResults: HTMLElement;
  private settingsSearch: SettingsSearch;
  private paletteEl: HTMLElement;
  private paletteInput: HTMLInputElement;
  private paletteHint: HTMLElement;
  private paletteResults: HTMLElement;
  private paletteSearch: SettingsSearch;
  private paletteOpen = false;
  private fovSlider: HTMLInputElement;
  private fovInput: HTMLInputElement;
  private fovValue: HTMLElement;
  private xhairEditor: CrosshairEditor;
  private weaponPanel: WeaponSettingsPanel;
  private adminPanel: AdminPanel;
  private cgazStylePanel: CgazStylePanel;
  private lastSlotSig = "";
  private textureModeSelect: HTMLSelectElement;
  private paniniSlider: HTMLInputElement;
  private paniniInput: HTMLInputElement;
  private paniniValue: HTMLElement;
  private hudEditor: HudEditor;
  private hudSpectatePlayer: HTMLInputElement;
  private openHudEditorBtn: HTMLButtonElement;
  private resetHudLayoutBtn: HTMLButtonElement;
  // Nickname (identity for leaderboards + the live server).
  private nickInput: HTMLInputElement;
  private nickSaveBtn: HTMLButtonElement;
  private nickStatusEl: HTMLElement;
  // Rules & tuning tab.
  private rulesListEl: HTMLElement;
  private rulesCategoriesEl: HTMLElement;
  private rulesSearchInput: HTMLInputElement;
  private ruleCategory: string | null = RULE_GROUPS[0]?.title ?? null;
  private ruleSearch = "";
  private rulePresetSelect: HTMLSelectElement;
  private rulePresetNoteEl: HTMLElement;
  private ruleDashBtn: HTMLInputElement;
  private ruleWjBtn: HTMLInputElement;
  private ruleSyncEl: HTMLElement;
  private rulePullBtn: HTMLButtonElement;
  private ruleVoteBtn: HTMLButtonElement;
  /** Last ruleset hash reported by the server (for the sync indicator). */
  private serverRulesHash: number | null = null;
  /** Set by the pull button; the next server ruleset is adopted locally. */
  private pullRulesPending = false;  private ruleProfileSelect: HTMLSelectElement;
  private ruleProfileName: HTMLInputElement;
  private ruleSaveBtn: HTMLButtonElement;
  private ruleLoadBtn: HTMLButtonElement;
  private ruleDeleteBtn: HTMLButtonElement;
  private ruleResetBtn: HTMLButtonElement;
  private ruleInputs = new Map<
    string,
    { range: HTMLInputElement; num: HTMLInputElement; reset: HTMLButtonElement; field: RuleField }
  >();
  private ruleGroupEls = new Map<string, HTMLElement>();
  /** Baseline values for the chosen preset (for the changed indicator). */
  private presetBaseMap: Record<string, number> = {};
  // Game-modes tab.
  private modesListEl: HTMLElement;
  private modePresetSelect: HTMLSelectElement;
  private modePresetNoteEl: HTMLElement;
  private modeProfileSelect: HTMLSelectElement;
  private modeProfileName: HTMLInputElement;
  private modeSaveBtn: HTMLButtonElement;
  private modeLoadBtn: HTMLButtonElement;
  private modeDeleteBtn: HTMLButtonElement;
  private modeResetBtn: HTMLButtonElement;
  private modeVoteBtn: HTMLButtonElement;
  private modeSyncEl: HTMLElement;
  private modePullBtn: HTMLButtonElement;
  private serverModeHash: number | null = null;
  private serverModeLabel = "";
  /** Set by the pull button; the next server mode is adopted locally. */
  private pullModePending = false;
  private modeInputs = new Map<
    keyof GameMode,
    { range?: HTMLInputElement; num?: HTMLInputElement; select?: HTMLSelectElement; text?: HTMLInputElement; toggle?: HTMLInputElement; field: ModeField }
  >();
  private leaderboardEl: HTMLElement;
  private playersEl: HTMLElement;
  private profileRatingsEl: HTMLElement;
  private profileMatchesEl: HTMLElement;
  private profileLoadedAt = 0;
  private profileToken = "";
  private playersTimer?: number;
  private playersLb: { nickname: string; time_ms: number }[] = [];
  private playersLbMap = "";
  private bindsEl: HTMLElement;
  private bindsResetBtn: HTMLButtonElement;
  /** Optional landing-page bind editor container. */
  private landingBindsEl: HTMLElement | null = null;
  private lbMapInput: HTMLInputElement;
  private lbRefreshBtn: HTMLButtonElement;
  private lbFavBtn: HTMLButtonElement;
  private voteInput: HTMLInputElement;
  private votePlayBtn: HTMLButtonElement;
  private voteResultEl: HTMLElement;
  private mapsSearchInput: HTMLInputElement;
  private mapsListEl: HTMLElement;
  private favoritesListEl: HTMLElement;
  private surfListEl: HTMLElement;
  /** Source maps discovered on the server (name + size bytes). */
  private sourceMaps: { name: string; size: number }[] | null = null;
  /** GameBanana surf/bhop catalog (downloadable). */
  private surfCatalog: { id: number; name: string; game: string }[] | null = null;
  // Sound tab.
  private soundListEl: HTMLElement;
  private soundStatusEl: HTMLElement;
  private soundReloadBtn: HTMLButtonElement;
  private volumeSlider: HTMLInputElement;
  private volumeValue: HTMLElement;
  private trueLightningSlider: HTMLInputElement;
  private trueLightningValue: HTMLElement;
  private trailSlider: HTMLInputElement;
  private trailValue: HTMLElement;
  private trailWidthSlider: HTMLInputElement;
  private trailWidthValue: HTMLElement;
  private trailGlowSlider: HTMLInputElement;
  private trailGlowValue: HTMLElement;
  private trailSelfModeSelect: HTMLSelectElement;
  private trailSelfColorInput: HTMLInputElement;
  private trailSelfTeamColorInput: HTMLInputElement;
  private trailEnemyModeSelect: HTMLSelectElement;
  private trailEnemyColorInput: HTMLInputElement;
  private trailEnemyTeamColorInput: HTMLInputElement;
  // Live tuning controls (Video / Network / Advanced).
  private dbgToggles: HTMLInputElement[] = [];
  private dbgSliders = new Map<string, { range: HTMLInputElement; value: HTMLElement }>();
  private dbgServerInput: HTMLInputElement | null = null;
  private dbgMapInput: HTMLInputElement | null = null;
  /** Downstream-rate picker (Network tab), persisted. */
  private downstreamSelects: HTMLSelectElement[] = [];
  /** Inline weapon keybind buttons on the Rules tab (action -> button). */
  private ruleBindButtons = new Map<string, HTMLButtonElement>();
  // Practice tab.
  private modeButtons: HTMLButtonElement[];
  private recallSlider: HTMLInputElement;
  private recallReadout: HTMLElement;
  private recallPrevBtn: HTMLButtonElement;
  private recallNextBtn: HTMLButtonElement;
  private recallRestartBtn: HTMLButtonElement;
  private recallHint: HTMLElement;
  private practiceBindsEl: HTMLElement;
  // Practice tab: position slots.
  private slotNameInput: HTMLInputElement;
  private slotSaveBtn: HTMLButtonElement;
  private slotListEl: HTMLElement;
  private ghostSourceSelect: HTMLSelectElement;
  private soundSelects = new Map<string, HTMLSelectElement>();
  private soundFiles: string[] = [];
  private soundLoaded = false;
  private soundLoading = false;
  private cb: SettingsCallbacks;
  private onToggle: (open: boolean) => void;
  private captureAction: string | null = null;
  /** Timestamp of the last mouse bind, so its contextmenu doesn't clear it. */
  private mouseBindAt = 0;
  /** Consume the contextmenu produced by a right-click that just set a bind. */
  private swallowNextContextMenu = false;
  private lbMap = "";
  private catalog: MapInfo[] | null = null;
  private catalogLoading = false;
  private mapResults: MapInfo[] = [];
  private packsListEl: HTMLElement;
  private packsRefreshBtn: HTMLButtonElement;
  private packs: MapPackInfo[] | null = null;
  private packsLoading = false;
  private mapRendered = 0;

  constructor(store: SettingsStore, cb: SettingsCallbacks, onToggle: (open: boolean) => void) {
    this.store = store;
    this.cb = cb;
    this.onToggle = onToggle;
    // A right-click used to bind Mouse2 must not immediately trip the bind
    // button's "right-click clears" handler. Consume exactly that contextmenu.
    window.addEventListener(
      "contextmenu",
      (e) => {
        if (!this.swallowNextContextMenu) return;
        this.swallowNextContextMenu = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );
    this.settings = store.value();
    // Apply saved per-weapon projectile colours/opacity immediately — the Rules
    // tab (which seeds them on render) may not have been opened yet.
    for (const [k, hex] of Object.entries(this.settings.projectileColors)) {
      const w = Number(k);
      if (w > 0) this.cb.onProjectileColor(w, hex);
    }
    for (const [k, a] of Object.entries(this.settings.projectileAlpha)) {
      const w = Number(k);
      if (w > 0) this.cb.onProjectileAlpha(w, a);
    }
    this.menuEl = document.getElementById("menu")!;
    this.settingsSearchInput = document.getElementById("settings-search-input") as HTMLInputElement;
    this.settingsSearchHint = document.getElementById("settings-search-hint")!;
    this.settingsSearchResults = document.getElementById("settings-search-results")!;
    window.addEventListener("keydown", (e) => {
      if (e.key !== "/") return;
      if (!this.menuEl.classList.contains("open")) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      this.settingsSearchInput.focus();
    });
    this.settingsSearch = new SettingsSearch(
      this.settingsSearchInput,
      this.settingsSearchHint,
      this.settingsSearchResults,
      this.settingsSearchCallbacks(),
    );
    // Command palette: the same search surface, in a standalone overlay that
    // can open while playing (the hotkey releases pointer lock).
    this.paletteEl = document.getElementById("palette")!;
    this.paletteInput = document.getElementById("palette-input") as HTMLInputElement;
    this.paletteHint = document.getElementById("palette-hint")!;
    this.paletteResults = document.getElementById("palette-results")!;
    this.paletteSearch = new SettingsSearch(
      this.paletteInput,
      this.paletteHint,
      this.paletteResults,
      this.settingsSearchCallbacks(),
    );
    this.paletteEl.addEventListener("click", (e) => {
      // Never let a palette click reach the document "click to lock" handler.
      e.stopPropagation();
      if (e.target === this.paletteEl) this.closePalette();
    });
    // Escape closes even when focus moved into a result control.
    document.addEventListener(
      "keydown",
      (e) => {
        if (!this.paletteOpen || e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        this.closePalette();
      },
      true,
    );
    this.fovSlider = document.getElementById("fov-slider") as HTMLInputElement;
    this.fovInput = document.getElementById("fov-input") as HTMLInputElement;
    this.fovValue = document.getElementById("fov-value")!;
    this.xhairEditor = new CrosshairEditor(
      document.getElementById("crosshair-editor")!,
      this.settings.crosshair,
      {
        onChange: (store) => {
          this.settings.crosshair = store;
          this.sanitizeWeaponSlots(store);
          this.save();
          this.cb.onCrosshair(store);
          this.cb.onWeaponSettings(this.settings.weaponSettings);
          this.syncWeaponPanel();
        },
      },
    );
    this.weaponPanel = new WeaponSettingsPanel(
      document.getElementById("weapon-settings")!,
      this.settings.weaponSettings,
      this.weaponGlobals(),
      this.slotRefs(),
      {
        onChange: (ws) => {
          this.settings.weaponSettings = ws;
          this.save();
          this.cb.onWeaponSettings(ws);
        },
        onEditCrosshair: (slotId) => {
          this.showPage("crosshair");
          this.xhairEditor.setActiveSlot(slotId);
        },
        onDefaultSensitivity: (sens) => this.setSensitivity(sens),
        getDebug: () => this.cb.getDebug() as unknown as Record<string, unknown>,
        getRawStatus: () => this.cb.getRawStatus(),
        onDebug: (key, value) => {
          this.cb.onDebug(key, value);
          this.refreshDebug();
        },
        onFov: (fov) => this.setFov(fov),
      },
    );
    this.adminPanel = new AdminPanel(document.getElementById("admin-panel")!, {
      getSettings: () => this.cb.getAdminSettings(),
      onApply: (settings) => this.cb.onAdminSettings(settings),
      onSave: (settings, token) => this.cb.onSaveAdminSettings(settings, token),
      getRole: () => this.cb.getRole(),
      onRole: (role) => this.cb.onRole(role),
    });
    this.textureModeSelect = document.getElementById("texture-mode") as HTMLSelectElement;
    this.paniniSlider = document.getElementById("panini-slider") as HTMLInputElement;
    this.paniniInput = document.getElementById("panini-input") as HTMLInputElement;
    this.paniniValue = document.getElementById("panini-value")!;
    this.hudSpectatePlayer = document.getElementById("hud-spectate-player") as HTMLInputElement;
    this.openHudEditorBtn = document.getElementById("open-hud-editor") as HTMLButtonElement;
    this.resetHudLayoutBtn = document.getElementById("reset-hud-layout") as HTMLButtonElement;
    this.hudEditor = new HudEditor(
      {
        onLive: (store, doc) => {
          this.settings.hudLayout = store;
          this.cb.onHudLayout(store, doc);
        },
        onCommit: (store, doc) => {
          this.settings.hudLayout = store;
          this.save();
          this.cb.onHudLayout(store, doc);
        },
        onTestValue: (source, value) => this.cb.onHudTestValue(source, value),
        onOpen: (open) => this.cb.onHudEditor(open),
      },
      this.settings.hudLayout,
    );
    this.cgazStylePanel = new CgazStylePanel(document.getElementById("cgaz-style")!, {
      getStyle: () => this.settings.hud.cgazStyle,
      onStyle: (style) => {
        this.settings.hud.cgazStyle = style;
        this.save();
        this.cb.onHud(this.hudSnapshot());
      },
    });
    this.nickInput = document.getElementById("nick-input") as HTMLInputElement;
    this.nickSaveBtn = document.getElementById("nick-save") as HTMLButtonElement;
    this.nickStatusEl = document.getElementById("nick-status")!;
    this.rulesListEl = document.getElementById("rules-list")!;
    this.rulesCategoriesEl = document.getElementById("rule-categories")!;
    this.rulesSearchInput = document.getElementById("rules-search") as HTMLInputElement;
    this.rulesSearchInput.addEventListener("input", () => {
      this.ruleSearch = this.rulesSearchInput.value;
      this.renderRules();
    });
    this.rulePresetSelect = document.getElementById("rule-preset") as HTMLSelectElement;
    this.rulePresetNoteEl = document.getElementById("rule-preset-note")!;
    this.ruleDashBtn = document.getElementById("rule-toggle-dash") as HTMLInputElement;
    this.ruleWjBtn = document.getElementById("rule-toggle-walljump") as HTMLInputElement;
    this.ruleSyncEl = document.getElementById("rule-sync")!;
    this.rulePullBtn = document.getElementById("rule-pull") as HTMLButtonElement;
    this.ruleVoteBtn = document.getElementById("rule-vote") as HTMLButtonElement;
    this.ruleProfileSelect = document.getElementById("rule-profile") as HTMLSelectElement;
    this.ruleProfileName = document.getElementById("rule-profile-name") as HTMLInputElement;
    this.ruleSaveBtn = document.getElementById("rule-save") as HTMLButtonElement;
    this.ruleLoadBtn = document.getElementById("rule-load") as HTMLButtonElement;
    this.ruleDeleteBtn = document.getElementById("rule-delete") as HTMLButtonElement;
    this.ruleResetBtn = document.getElementById("rule-reset") as HTMLButtonElement;
    this.modesListEl = document.getElementById("modes-list")!;
    this.modePresetSelect = document.getElementById("mode-preset") as HTMLSelectElement;
    this.modePresetNoteEl = document.getElementById("mode-preset-note")!;
    this.modeProfileSelect = document.getElementById("mode-profile") as HTMLSelectElement;
    this.modeProfileName = document.getElementById("mode-profile-name") as HTMLInputElement;
    this.modeSaveBtn = document.getElementById("mode-save") as HTMLButtonElement;
    this.modeLoadBtn = document.getElementById("mode-load") as HTMLButtonElement;
    this.modeDeleteBtn = document.getElementById("mode-delete") as HTMLButtonElement;
    this.modeResetBtn = document.getElementById("mode-reset") as HTMLButtonElement;
    this.modeVoteBtn = document.getElementById("mode-vote") as HTMLButtonElement;
    this.modeSyncEl = document.getElementById("mode-sync")!;
    this.modePullBtn = document.getElementById("mode-pull") as HTMLButtonElement;
    this.leaderboardEl = document.getElementById("leaderboard")!;
    this.playersEl = document.getElementById("players")!;
    this.profileRatingsEl = document.getElementById("profile-ratings")!;
    this.profileMatchesEl = document.getElementById("profile-matches")!;
    this.bindsEl = document.getElementById("binds")!;
    this.bindsResetBtn = document.getElementById("binds-reset") as HTMLButtonElement;
    this.lbMapInput = document.getElementById("lb-map-input") as HTMLInputElement;
    this.lbRefreshBtn = document.getElementById("lb-refresh") as HTMLButtonElement;
    this.packsListEl = document.getElementById("packs-list")!;
    this.packsRefreshBtn = document.getElementById("packs-refresh") as HTMLButtonElement;
    this.lbFavBtn = document.getElementById("lb-fav") as HTMLButtonElement;
    this.voteInput = document.getElementById("vote-input") as HTMLInputElement;
    this.votePlayBtn = document.getElementById("vote-play") as HTMLButtonElement;
    this.voteResultEl = document.getElementById("vote-result")!;
    this.mapsSearchInput = document.getElementById("maps-search-input") as HTMLInputElement;
    this.mapsListEl = document.getElementById("maps-list")!;
    this.favoritesListEl = document.getElementById("favorites-list")!;
    this.surfListEl = document.getElementById("surf-list")!;
    this.soundListEl = document.getElementById("sound-list")!;
    this.soundStatusEl = document.getElementById("sound-status")!;
    this.soundReloadBtn = document.getElementById("sound-reload") as HTMLButtonElement;
    this.volumeSlider = document.getElementById("volume-slider") as HTMLInputElement;
    this.volumeValue = document.getElementById("volume-value")!;
    this.trueLightningSlider = document.getElementById("truelightning-slider") as HTMLInputElement;
    this.trueLightningValue = document.getElementById("truelightning-value")!;
    this.trailSlider = document.getElementById("trail-slider") as HTMLInputElement;
    this.trailValue = document.getElementById("trail-value")!;
    this.trailWidthSlider = document.getElementById("trail-width") as HTMLInputElement;
    this.trailWidthValue = document.getElementById("trail-width-value")!;
    this.trailGlowSlider = document.getElementById("trail-glow") as HTMLInputElement;
    this.trailGlowValue = document.getElementById("trail-glow-value")!;
    this.trailSelfModeSelect = document.getElementById("trail-self-mode") as HTMLSelectElement;
    this.trailSelfColorInput = document.getElementById("trail-self-color") as HTMLInputElement;
    this.trailSelfTeamColorInput = document.getElementById("trail-self-team-color") as HTMLInputElement;
    this.trailEnemyModeSelect = document.getElementById("trail-enemy-mode") as HTMLSelectElement;
    this.trailEnemyColorInput = document.getElementById("trail-enemy-color") as HTMLInputElement;
    this.trailEnemyTeamColorInput = document.getElementById("trail-enemy-team-color") as HTMLInputElement;
    this.modeButtons = Array.from(this.menuEl.querySelectorAll<HTMLButtonElement>(".mode-btn"));
    this.recallSlider = document.getElementById("recall-slider") as HTMLInputElement;
    this.recallReadout = document.getElementById("recall-readout")!;
    this.recallPrevBtn = document.getElementById("recall-prev") as HTMLButtonElement;
    this.recallNextBtn = document.getElementById("recall-next") as HTMLButtonElement;
    this.recallRestartBtn = document.getElementById("recall-restart") as HTMLButtonElement;
    this.recallHint = document.getElementById("recall-hint")!;
    this.practiceBindsEl = document.getElementById("practice-binds")!;
    this.slotNameInput = document.getElementById("slot-name") as HTMLInputElement;
    this.slotSaveBtn = document.getElementById("slot-save") as HTMLButtonElement;
    this.slotListEl = document.getElementById("slot-list")!;
    this.ghostSourceSelect = document.getElementById("ghost-source") as HTMLSelectElement;

    this.bind();
    this.bindTabs();
    this.bindDebug();
    this.bindDownstream();
    this.bindVfxColors();
    this.bindConfigButtons();
    this.bindLeaderboardControls();
    this.bindMapsControls();
    this.bindSoundControls();
    this.bindPracticeControls();
    this.bindRulesControls();
    this.bindModesControls();
    this.applyAll();
    // The landing page has its own compact, editable essential-bind panel.
    const landingBinds = document.getElementById("landing-binds");
    if (landingBinds) this.mountLandingBinds(landingBinds);
    // Preload the drop-in sound list so the dropdowns are ready when opened.
    void this.ensureSoundsLoaded();
    // Prime the default page: the markup already marks Maps as active, so
    // nothing would otherwise run its entry hook (catalog fetch + sub-nav
    // display) until the player switches to another tab and back.
    this.showPage("maps");
  }

  /** Tab/page switching. */
  private bindTabs() {
    for (const tab of Array.from(this.menuEl.querySelectorAll<HTMLButtonElement>(".tabs .tab"))) {
      tab.addEventListener("click", () => {
        if (tab.dataset.nav === "home") {
          this.cb.onHome();
          return;
        }
        this.showPage(tab.dataset.page);
      });
    }
    // Sub-navigations (Settings, Maps) are declared with `data-group`; showPage
    // reveals the group that matches the active page.
    for (const subnav of Array.from(this.menuEl.querySelectorAll<HTMLElement>(".subtabs"))) {
      for (const b of Array.from(subnav.querySelectorAll<HTMLButtonElement>(".subtab"))) {
        b.addEventListener("click", () => this.showPage(b.dataset.page));
      }
    }
  }

  /** The top-level/sub-nav group a page belongs to (undefined = top level). */
  private navGroup(pageId: string | undefined): string | undefined {
    return pageId ? NAV_GROUPS[pageId] : undefined;
  }

  /** Reset the menu to its default Maps page (called after a new map loads). */
  resetToMaps(): void {
    this.showPage("maps");
  }

  /** Show a page, run its side effects, and sync both navigation levels. */
  private showPage(pageId: string | undefined) {
    this.settingsSearch.close();
    const pages = Array.from(this.menuEl.querySelectorAll<HTMLElement>(".page"));
    for (const p of pages) p.classList.toggle("active", p.id === `page-${pageId}`);
    if (pageId === "maps") this.ensureCatalogAndRender();
    if (pageId === "favorites") this.ensureCatalogAndRenderFavorites();
    if (pageId === "surf") this.ensureSourceMapsAndRender();
    if (pageId === "packs") void this.renderPacks();
    if (pageId === "audio") void this.ensureSoundsLoaded();
    if (pageId === "settings") this.refreshNickname();
    if (pageId === "rules") {
      this.refreshProfiles();
      this.refreshPreset();
      this.renderRules();
    }
    if (pageId === "modes") {
      this.refreshModePreset();
      this.refreshModeProfiles();
      this.renderModes();
      this.refreshModeSync();
    }
    if (pageId === "practice") {
      this.refreshRecall();
      this.refreshSlots();
    }
    // Video/Network/Advanced hold live tuning controls; refresh them on entry.
    if (pageId === "video" || pageId === "advanced") {
      this.refreshDebug();
    }
    // Crosshair editor preview loop runs only while its page is visible.
    // The crosshair editor lives on the HUD page now.
    if (pageId === "admin") this.adminPanel.refresh();
    if (pageId === "crosshair" || pageId === "hud") this.xhairEditor.activate();
    else this.xhairEditor.deactivate();
    if (pageId === "weapons") this.weaponPanel.activate();
    if (pageId === "players") {
      void this.renderPlayers();
      if (this.playersTimer === undefined) {
        this.playersTimer = window.setInterval(() => {
          const el = document.getElementById("page-players");
          if (el && el.classList.contains("active")) void this.renderPlayers();
        }, 2000);
      }
    }
    const group = this.navGroup(pageId);
    // Top-level tab stays lit while browsing one of its sub-pages.
    for (const t of Array.from(
      this.menuEl.querySelectorAll<HTMLButtonElement>(".tabs:not(.subtabs) .tab"),
    )) {
      const page = t.dataset.page;
      const active = page === pageId || (group !== undefined && this.navGroup(page) === group);
      t.classList.toggle("active", active);
      t.classList.toggle("tab-active", active);
    }
    // Show only the matching sub-nav.
    for (const subnav of Array.from(this.menuEl.querySelectorAll<HTMLElement>(".subtabs"))) {
      const show = group !== undefined && subnav.dataset.group === group;
      subnav.style.display = show ? "flex" : "none";
      for (const b of Array.from(subnav.querySelectorAll<HTMLButtonElement>(".subtab"))) {
        const on = b.dataset.page === pageId;
        b.classList.toggle("active", on);
        b.classList.toggle("tab-active", on);
      }
    }
  }

  /** Map packs: subfolders of `maps/` on the server. */
  async renderPacks(force = false) {
    if (this.packsLoading) return;
    this.packsLoading = true;
    try {
      if (force || !this.packs) {
        this.packsListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">loading map packs…</div>`;
        this.packs = await getMapPacks();
      }
      this.packsListEl.innerHTML = "";
      const packs = this.packs ?? [];
      if (!packs.length) {
        const empty = document.createElement("div");
        empty.className = "map-row";
        empty.style.color = "var(--ui-text-faint)";
        empty.textContent = "no map packs yet — drop a folder into maps/ on the server";
        this.packsListEl.append(empty);
        return;
      }
      for (const pack of packs) {
        const head = document.createElement("div");
        head.className = "map-pack-head";
        const title = document.createElement("div");
        title.className = "map-pack-title";
        title.textContent = pack.name || pack.folder;
        const meta = document.createElement("div");
        meta.className = "map-pack-meta";
        const n = pack.maps.length;
        meta.textContent = `${n} map${n === 1 ? "" : "s"} · maps/${pack.folder}${pack.author ? ` · ${pack.author}` : ""}`;
        head.append(title, meta);
        if (pack.description) {
          const desc = document.createElement("div");
          desc.className = "map-pack-desc";
          desc.textContent = pack.description;
          head.append(desc);
        }
        this.packsListEl.append(head);
        for (const m of pack.maps) this.packsListEl.append(this.makeMapRow(m));
      }
    } catch (e) {
      this.packs = null;
      this.packsListEl.innerHTML = `<div class="map-row" style="color:var(--ui-danger)">map packs unavailable: ${(e as Error).message}</div>`;
    } finally {
      this.packsLoading = false;
    }
  }

  /** Load the catalog (once) and render the maps list. */
  async ensureCatalogAndRender() {
    try {
      await this.getCatalog();
      this.renderMapsList();
    } catch {
      this.mapsListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">catalog unavailable</div>`;
    }
  }

  async ensureCatalogAndRenderFavorites() {
    try {
      await this.getCatalog();
      this.renderFavorites();
    } catch {
      this.favoritesListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">catalog unavailable</div>`;
    }
  }

  /** Fetch the server's Source map list + the GameBanana download catalog. */
  async ensureSourceMapsAndRender() {
    this.surfListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">loading…</div>`;
    const get = (p: string) =>
      fetch(api(p))
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({}));
    const [local, remote] = await Promise.all([
      get("/maps/source-index.json"),
      get("/maps/surf-catalog.json"),
    ]);
    this.sourceMaps = (local as { maps?: { name: string; size: number }[] }).maps ?? [];
    this.surfCatalog = (remote as { items?: { id: number; name: string; game: string }[] }).items ?? [];
    this.renderSourceMaps();
  }

  private makeDownloadRow(
    m: { id: number; name: string; game: string },
    installed: boolean,
  ): HTMLButtonElement {
    const row = document.createElement("button");
    row.className = "map-row";
    const name = document.createElement("span");
    name.className = "map-name";
    name.textContent = m.name;
    const meta = document.createElement("span");
    meta.className = "map-author";
    meta.textContent = installed ? "installed · click to play" : m.game || "GameBanana";
    row.append(name, meta);
    row.addEventListener("click", async () => {
      if (installed) {
        this.launchMap(m.name);
        return;
      }
      meta.textContent = "downloading…";
      row.disabled = true;
      try {
        const token = loadAdminToken();
        const r = await fetch(api("/maps/surf-download.json?id=" + encodeURIComponent(String(m.id))), {
          headers: token ? { "x-webrace-internal": token } : {},
        });
        const j = r.ok ? ((await r.json()) as { ok?: boolean; name?: string }) : null;
        if (!j?.ok || !j.name) throw new Error("failed");
        await this.ensureSourceMapsAndRender();
        this.launchMap(j.name);
      } catch {
        meta.textContent = "download failed";
        row.disabled = false;
      }
    });
    return row;
  }

  private renderSourceMaps() {
    const local = this.sourceMaps ?? [];
    const remote = this.surfCatalog ?? [];
    this.surfListEl.innerHTML = "";
    const header = (t: string) => {
      const h = document.createElement("div");
      h.className = "cat-header";
      h.textContent = t;
      this.surfListEl.appendChild(h);
    };
    if (local.length === 0 && remote.length === 0) {
      this.surfListEl.innerHTML =
        `<div class="map-row" style="color:var(--ui-text-faint);white-space:normal">no Source maps yet — download one below (needs network), or drop a .bsp into maps/source/ on the server</div>`;
      return;
    }
    if (local.length > 0) {
      header("installed");
      for (const m of local) {
        const mb = m.size ? `${(m.size / 1048576).toFixed(1)} MB` : "";
        this.surfListEl.appendChild(this.makeMapRow(stubMapInfo(m.name, { game: "source", author: mb })));
      }
    }
    if (remote.length > 0) {
      header("download · GameBanana (CS:S / CS:GO)");
      const installed = new Set(local.map((m) => m.name.toLowerCase()));
      for (const m of remote) {
        this.surfListEl.appendChild(this.makeDownloadRow(m, installed.has(m.name.toLowerCase())));
      }
    }
  }

  /** The map the leaderboard favorite star acts on (input, else last loaded). */
  private lbFavMap(): string {
    return (this.lbMapInput.value.trim() || this.lbMap).toLowerCase();
  }

  /** Sync the leaderboard favorite star with the current map. */
  private updateLbFav() {
    const name = this.lbFavMap();
    const fav = name !== "" && isFavorite(name);
    this.lbFavBtn.textContent = fav ? "★" : "☆";
    this.lbFavBtn.title = fav ? "remove from favorites" : "add to favorites";
    this.lbFavBtn.disabled = name === "";
  }

  /** Leaderboard controls (map input + favorite + refresh). */
  private bindLeaderboardControls() {
    this.lbRefreshBtn.addEventListener("click", () => {
      const map = this.lbMapInput.value.trim();
      if (map) void this.refreshLeaderboard(map);
    });
    this.lbMapInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        const map = this.lbMapInput.value.trim();
        if (map) void this.refreshLeaderboard(map);
      }
    });
    this.lbMapInput.addEventListener("input", () => this.updateLbFav());
    // Trophy next to the map field: jump straight to the leaderboard sub-tab.
    document.getElementById("lb-board")?.addEventListener("click", () => {
      const map = this.lbMapInput.value.trim();
      this.showPage("leaderboards");
      if (map) void this.refreshLeaderboard(map);
    });
    this.lbFavBtn.addEventListener("click", () => {
      const name = this.lbFavMap();
      if (!name) return;
      toggleFavorite(name);
      this.updateLbFav();
      // Keep the Favorites tab in sync if it has been rendered.
      if (this.catalog) this.renderFavorites();
    });
    this.updateLbFav();
  }

  /** Map voting + search + favorites controls. */
  private bindMapsControls() {
    const doVote = async (explicit?: string) => {
      const input = (explicit ?? this.voteInput.value).trim();
      if (!input) return;
      if (explicit === undefined) this.voteInput.value = input;
      const maps = await this.getCatalog();
      const req = parseVote(input);
      if (!req) {
        this.voteResultEl.textContent = "invalid vote";
        return;
      }
      const map = resolveVote(maps, req);
      if (!map) {
        this.voteResultEl.textContent = `no map matched: ${input}`;
        return;
      }
      this.voteResultEl.textContent = `voting… ${map}`;
      this.cb.onPlayMap(map);
    };
    this.votePlayBtn.addEventListener("click", () => void doVote());
    this.voteInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void doVote();
    });

    // Clickable example vote chips.
    this.menuEl.querySelectorAll<HTMLButtonElement>(".vote-chip").forEach((chip) => {
      chip.addEventListener("click", () => void doVote(chip.dataset.vote));
    });

    this.mapsSearchInput.addEventListener("input", () => this.renderMapsList());

    this.mapsListEl.addEventListener("scroll", this.onMapsListScroll);
    this.packsRefreshBtn.addEventListener("click", () => void this.renderPacks(true));
  }

  /** Sound tab: reload button + live file list. */
  private bindSoundControls() {
    this.soundReloadBtn.addEventListener("click", () => void this.ensureSoundsLoaded(true));
    document.getElementById("sound-reset")?.addEventListener("click", () => {
      this.settings.sounds = { ...DEFAULTS.sounds };
      this.save();
      this.renderSounds();
      this.cb.onSounds({ ...this.settings.sounds });
    });
  }

  /** Practice tab: mode buttons + recall keyframe stepping. */
  private bindPracticeControls() {
    for (const btn of this.modeButtons) {
      btn.addEventListener("click", () => this.cb.onMode(Number(btn.dataset.mode)));
    }
    this.recallPrevBtn.addEventListener("click", () => this.cb.onRecallStep(-1));
    this.recallNextBtn.addEventListener("click", () => this.cb.onRecallStep(1));
    this.recallSlider.addEventListener("input", () =>
      this.cb.onRecallSelect(Number(this.recallSlider.value)),
    );
    this.recallRestartBtn.addEventListener("click", () => this.cb.onRestart());
    this.ghostSourceSelect.addEventListener("change", () =>
      this.cb.onGhostSource(this.ghostSourceSelect.value),
    );

    const saveSlot = () => {
      const name = this.slotNameInput.value.trim();
      if (!name) return;
      this.cb.onSaveSlot(name);
      this.slotNameInput.value = "";
      this.refreshSlots();
    };
    this.slotSaveBtn.addEventListener("click", saveSlot);
    this.slotNameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") saveSlot();
    });
  }

  /** Render the named position slots. */
  refreshSlots() {
    if (!this.slotListEl) return;
    const names = this.cb.getSlots();
    this.slotListEl.innerHTML = "";
    if (names.length === 0) {
      this.slotListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">no slots saved</div>`;
      return;
    }
    for (const name of names) {
      const row = document.createElement("button");
      row.className = "map-row";
      const label = document.createElement("span");
      label.className = "map-name";
      label.textContent = name;
      const del = document.createElement("span");
      del.className = "fav-star";
      del.textContent = "×";
      del.title = "delete slot";
      row.append(label, del);
      row.addEventListener("click", () => this.cb.onRecallSlot(name));
      del.addEventListener("click", (e) => {
        e.stopPropagation();
        this.cb.onDeleteSlot(name);
        this.refreshSlots();
      });
      this.slotListEl.appendChild(row);
    }
  }

  /** Sync the mode buttons + recall slider/readout from the live session. */
  refreshRecall() {
    this.ghostSourceSelect.value = this.cb.getGhostSource();
    const info = this.cb.getRecall();
    if (!info) {
      for (const b of this.modeButtons) b.classList.toggle("active", b.dataset.mode === "0");
      this.recallSlider.disabled = true;
      this.recallPrevBtn.disabled = true;
      this.recallNextBtn.disabled = true;
      this.recallRestartBtn.disabled = true;
      this.recallReadout.textContent = "no session";
      this.recallHint.textContent = "";
      return;
    }
    for (const b of this.modeButtons) {
      b.classList.toggle("active", Number(b.dataset.mode) === info.mode);
    }
    const hasKf = info.count > 0;
    this.recallSlider.disabled = !hasKf;
    this.recallPrevBtn.disabled = !hasKf;
    this.recallNextBtn.disabled = !hasKf;
    this.recallRestartBtn.disabled = info.mode !== 3;
    if (!hasKf) {
      this.recallReadout.textContent = "no recording";
      this.recallHint.textContent = "";
      return;
    }
    this.recallSlider.min = "0";
    this.recallSlider.max = String(Math.max(0, info.count - 1));
    this.recallSlider.value = String(info.index);
    this.updateRecallReadout(info.index, info.count, info.tick);
    this.recallHint.textContent =
      info.mode === 3
        ? "frozen in time · scroll or [ / ] to step · 4 starts the run here"
        : "enter Recall to scrub the last run";
  }

  private updateRecallReadout(index: number, count: number, tick: number) {
    const ms = ticksToMs(tick);
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const cs = Math.floor((ms % 1000) / 10);
    const t = `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
    this.recallReadout.textContent = `${index + 1} / ${count}  ·  ${t}`;
  }

  /**
   * Fetch the list of files under `snd/` (once, or when forced by Reload) and
   * render the per-event dropdowns.
   */
  async ensureSoundsLoaded(force = false) {
    if (this.soundLoading) return;
    if (this.soundLoaded && !force) {
      this.renderSounds();
      return;
    }
    this.soundLoading = true;
    this.soundStatusEl.textContent = "loading…";
    try {
      this.soundFiles = await this.cb.onListSounds();
      this.soundLoaded = true;
      this.soundStatusEl.textContent = this.soundFiles.length
        ? `${this.soundFiles.length} file${this.soundFiles.length === 1 ? "" : "s"}`
        : "no files found in snd/";
    } catch {
      this.soundFiles = [];
      this.soundStatusEl.textContent = "could not load snd/ (is the map-server running?)";
    } finally {
      this.soundLoading = false;
    }
    this.renderSounds();
  }

  /** Build a labelled dropdown + preview button for every sound event. */
  private renderSounds() {
    if (!this.soundListEl) return;
    this.soundListEl.innerHTML = "";
    this.soundSelects.clear();

    for (const ev of SOUND_EVENTS) {
      const row = document.createElement("div");
      row.className = "sound-row";

      const label = document.createElement("span");
      label.className = "sound-label";
      label.textContent = ev.label;

      const select = document.createElement("select");
      select.className = "sound-select";
      select.dataset.setting = `sounds.${ev.id}`;
      const none = document.createElement("option");
      none.value = "";
      none.textContent = this.soundFiles.length ? "— none —" : "(no files in snd/)";
      select.append(none);
      for (const f of this.soundFiles) {
        const opt = document.createElement("option");
        opt.value = f;
        opt.textContent = f;
        select.append(opt);
      }
      const cur = (this.settings.sounds ?? {})[ev.id] ?? "";
      if (cur && this.soundFiles.includes(cur)) select.value = cur;
      select.addEventListener("change", () => {
        if (!this.settings.sounds || typeof this.settings.sounds !== "object") {
          this.settings.sounds = {};
        }
        if (select.value) this.settings.sounds[ev.id] = select.value;
        else delete this.settings.sounds[ev.id];
        this.save();
        this.cb.onSounds({ ...this.settings.sounds });
      });
      this.soundSelects.set(ev.id, select);

      const play = document.createElement("button");
      play.className = "sound-play";
      play.textContent = "▶";
      dsElement(play, "button", { style: "ghost", size: "xs", shape: "square", keep: "sound-play" });
      play.title = "preview selected file";
      play.addEventListener("click", () => {
        const file = select.value;
        if (file) this.cb.onPlaySound(file);
      });

      row.append(label, select, play);
      this.soundListEl.append(row);
    }
  }

  async getCatalog(): Promise<MapInfo[]> {
    if (!this.catalog) this.catalog = await getCatalog();
    return this.catalog;
  }

  /** Map-name completions for `vote map <prefix>` in the search/palette. */
  private mapSuggestions(query: string): Array<{ name: string; detail: string }> {
    if (!this.catalog) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const rank = (m: MapInfo): number => {
      const name = m.map_name.toLowerCase();
      if (name === q) return 0;
      if (name.startsWith(q)) return 1;
      if (name.includes(q)) return 2;
      if ((m.normalized_name ?? "").toLowerCase().includes(q)) return 3;
      if ((m.longname ?? "").toLowerCase().includes(q)) return 4;
      if ((m.author ?? "").toLowerCase().includes(q)) return 5;
      if ((m.game ?? "").toLowerCase().includes(q)) return 6;
      return 99;
    };
    return this.catalog
      .map((m) => ({ m, r: rank(m) }))
      .filter((x) => x.r < 99)
      .sort((a, b) => a.r - b.r || a.m.map_name.localeCompare(b.m.map_name))
      .slice(0, 8)
      .map(({ m }) => ({
        name: m.map_name,
        detail: [m.longname, m.author].filter(Boolean).join(" · "),
      }));
  }

  /** Load the catalog on demand; both search surfaces re-render when it lands. */
  private ensureMapSuggestions(): void {
    if (this.catalog || this.catalogLoading) return;
    this.catalogLoading = true;
    void this.getCatalog()
      .then(() => {
        this.catalogLoading = false;
        this.settingsSearch.refresh();
        this.paletteSearch.refresh();
      })
      .catch(() => {
        this.catalogLoading = false;
      });
  }

  /** Render the searchable map list (incremental: load more on scroll). */
  renderMapsList() {
    if (!this.catalog) return;
    const q = this.mapsSearchInput.value.trim();
    const results = searchMaps(this.catalog, q);
    this.mapResults = results;
    this.mapRendered = 0;
    this.mapsListEl.innerHTML = "";
    this.appendMapBatch();
  }

  private appendMapBatch() {
    if (!this.catalog) return;
    const BATCH = 100;
    const end = Math.min(this.mapRendered + BATCH, this.mapResults.length);
    for (let i = this.mapRendered; i < end; i++) {
      this.mapsListEl.appendChild(this.makeMapRow(this.mapResults[i]));
    }
    this.mapRendered = end;
  }

  /** Load more when the list is scrolled near the bottom. */
  private onMapsListScroll = () => {
    const el = this.mapsListEl;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      this.appendMapBatch();
    }
  };

  renderFavorites() {
    if (!this.catalog) {
      this.favoritesListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">loading…</div>`;
      return;
    }
    const byName = new Map(this.catalog.map((m) => [m.map_name.toLowerCase(), m]));
    // Names the catalog doesn't know (or an unavailable catalog) still render,
    // so known test maps and favourites are always visible and launchable.
    const resolve = (n: string): MapInfo => byName.get(n) ?? stubMapInfo(n);
    const testing = TESTING_MAPS.map(resolve);
    const user = getFavorites()
      .filter((n) => !TESTING_MAPS.includes(n))
      .map(resolve);
    if (testing.length === 0 && user.length === 0) {
      this.favoritesListEl.innerHTML = `<div class="map-row" style="color:var(--ui-text-faint)">no favorites yet — click ☆ on a map</div>`;
      return;
    }
    this.favoritesListEl.innerHTML = "";
    const addSection = (title: string, maps: MapInfo[]) => {
      if (maps.length === 0) return;
      const header = document.createElement("div");
      header.className = "cat-header";
      header.textContent = title;
      this.favoritesListEl.appendChild(header);
      for (const m of maps) this.favoritesListEl.appendChild(this.makeMapRow(m));
    };
    addSection("testing", testing);
    addSection("your favorites", user);
  }

  private makeMapRow(m: MapInfo): HTMLButtonElement {
    const row = document.createElement("button");
    row.className = "map-row";

    // Star (favorite toggle).
    const star = document.createElement("span");
    star.className = "fav-star";
    star.textContent = isFavorite(m.map_name) ? "★" : "☆";
    star.title = "toggle favorite";

    const name = document.createElement("span");
    name.className = "map-name";
    name.textContent = m.map_name;

    const author = document.createElement("span");
    author.className = "map-author";
    author.textContent = m.author ?? m.game ?? "";

    const tags: string[] = [];
    if (m.has_slick) tags.push("slick");
    if (m.has_rocket_launcher) tags.push("rocket");
    if (m.has_plasmagun) tags.push("plasma");
    if (m.has_grenade_launcher) tags.push("grenade");
    if (m.has_jumppad) tags.push("jp");
    const tagsEl = document.createElement("span");
    tagsEl.className = "map-tags";
    tagsEl.textContent = tags.join(" ");

    row.append(star, name, author, tagsEl);

    // Click = play the map (applying the prefix-implied movement preset).
    row.addEventListener("click", () => this.launchMap(m.map_name));

    // Star click = toggle favorite (stop propagation).
    star.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleFavorite(m.map_name);
      star.textContent = isFavorite(m.map_name) ? "★" : "☆";
      this.renderFavorites();
    });

    return row;
  }

  /** The current dev/tuning rule set (for pushing to the server on connect). */
  getRules(): Record<string, number> {
    return { ...this.settings.rules };
  }

  /** Apply a rules preset by id (mirrors the Rules tab select). */
  applyPreset(id: string) {
    if (this.settings.rulePreset === id) return;
    this.settings.rulePreset = id;
    this.settings.rules = presetValues(id);
    this.save();
    this.refreshPreset();
    this.renderRules();
    this.cb.onRules({ ...this.settings.rules });
  }

  /**
   * Console/search: apply a rules preset and propose it as a vote.
   * Returns the preset label, or null for an unknown id.
   */
  votePreset(id: string): string | null {
    if (!PRESETS.some((p) => p.id === id)) return null;
    this.applyPreset(id);
    const label = presetLabel(id);
    this.cb.onVoteRules({ ...this.settings.rules }, label);
    return label;
  }

  /**
   * Console/search: resolve a map vote string (`random`, `random slick`,
   * `map <name>`, bare name) against the catalog and propose it.
   * Returns the concrete map name, or null when nothing matched.
   */
  async voteMap(input: string): Promise<string | null> {
    const q = input.trim();
    if (!q) return null;
    const maps = await this.getCatalog();
    const req = parseVote(q);
    if (!req) return null;
    const map = resolveVote(maps, req);
    if (!map) return null;
    this.cb.onPlayMap(map);
    return map;
  }

  /**
   * Console/search: apply a mode preset and propose it as a vote.
   * Returns the mode label, or null for an unknown id.
   */
  voteModePreset(id: string): string | null {
    const preset = MODE_PRESETS.find((p) => p.id === id);
    if (!preset) return null;
    const family = this.cb.getServerFamily();
    const isRace = preset.mode.structure === "race";
    if ((family === "race" && !isRace) || (family === "combat" && isRace)) return null;
    this.applyModePreset(id);
    const label = modeLabel(this.settings.mode);
    this.cb.onVoteGameMode(
      { ...this.settings.mode },
      { ...this.settings.rules },
      label,
    );
    return label;
  }

  /** Launch a map, first applying the movement preset its name implies. */
  private launchMap(name: string) {
    const preset = presetForMap(name);
    if (preset) this.applyPreset(preset);
    this.cb.onPlayMap(name);
  }

  /** Wire the Rules tab's preset + profile + toggle controls. */
  private bindRulesControls() {
    this.ruleDashBtn.addEventListener("change", () => {
      this.applyRules(this.ruleDashBtn.checked ? DASH_ON : DASH_OFF);
    });
    this.ruleWjBtn.addEventListener("change", () => {
      this.applyRules(this.ruleWjBtn.checked ? WJ_ON : WJ_OFF);
    });
    this.rulePullBtn.addEventListener("click", () => {
      this.pullRulesPending = true;
      this.cb.onRequestRules();
    });
    this.ruleVoteBtn.addEventListener("click", () =>
      this.cb.onVoteRules({ ...this.settings.rules }, presetLabel(this.settings.rulePreset)),
    );
    this.rulePresetSelect.addEventListener("change", () => {
      const id = this.rulePresetSelect.value;
      this.settings.rulePreset = id;
      this.settings.rules = presetValues(id);
      this.save();
      this.refreshPreset();
      this.renderRules();
      this.cb.onRules({ ...this.settings.rules });
    });
    this.ruleSaveBtn.addEventListener("click", () => {
      const name = this.ruleProfileName.value.trim();
      if (!name) return;
      const profiles = loadProfiles();
      profiles[name] = { ...this.settings.rules };
      saveProfiles(profiles);
      this.ruleProfileName.value = "";
      this.refreshProfiles();
      this.ruleProfileSelect.value = name;
    });
    this.ruleLoadBtn.addEventListener("click", () => {
      const name = this.ruleProfileSelect.value;
      const saved = name ? loadProfiles()[name] : null;
      if (!saved) return;
      this.settings.rules = { ...ruleDefaults(), ...saved };
      this.save();
      this.renderRules();
      this.cb.onRules({ ...this.settings.rules });
    });
    this.ruleDeleteBtn.addEventListener("click", () => {
      const name = this.ruleProfileSelect.value;
      if (!name) return;
      const profiles = loadProfiles();
      delete profiles[name];
      saveProfiles(profiles);
      this.refreshProfiles();
    });
    this.ruleResetBtn.addEventListener("click", () => {
      this.settings.rules = { ...this.presetBaseMap };
      this.save();
      this.renderRules();
      this.cb.onRules({ ...this.settings.rules });
    });
  }

  // ---- Game modes --------------------------------------------------------

  /** The current mode (match rules). */
  getMode(): GameMode {
    return { ...this.settings.mode };
  }

  getModeLabel(): string {
    return modeLabel(this.settings.mode);
  }

  /** Populate the mode-preset dropdown. */
  refreshModePreset() {
    const known = MODE_PRESETS.some((p) => p.id === this.settings.modePreset);
    if (!known && modePresetId(this.settings.mode) === null) {
      this.settings.modePreset = "custom";
    } else if (!known) {
      this.settings.modePreset = modePresetId(this.settings.mode) ?? "race";
    }
    this.modePresetSelect.innerHTML = "";
    for (const p of MODE_PRESETS) {
      const o = document.createElement("option");
      o.value = p.id;
      o.textContent = p.label;
      this.modePresetSelect.append(o);
    }
    if (this.settings.modePreset === "custom") {
      const o = document.createElement("option");
      o.value = "custom";
      o.textContent = "Custom";
      this.modePresetSelect.append(o);
    }
    this.modePresetSelect.value = this.settings.modePreset;
    const p = MODE_PRESETS.find((x) => x.id === this.settings.modePreset);
    this.modePresetNoteEl.textContent = p
      ? `${p.label}: ${modeLabel(this.settings.mode)}`
      : "Custom mode (edits are kept as a profile)";
  }

  /** Populate the saved-mode profile dropdown. */
  refreshModeProfiles() {
    const profiles = loadModeProfiles();
    const keep = this.modeProfileSelect.value;
    this.modeProfileSelect.innerHTML = "";
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "— profile —";
    this.modeProfileSelect.append(blank);
    for (const name of Object.keys(profiles).sort()) {
      const o = document.createElement("option");
      o.value = name;
      o.textContent = name;
      this.modeProfileSelect.append(o);
    }
    if (keep && profiles[keep]) this.modeProfileSelect.value = keep;
  }

  /** Wire the Game modes tab's preset/profile/vote controls. */
  private bindModesControls() {
    this.modePresetSelect.addEventListener("change", () =>
      this.applyModePreset(this.modePresetSelect.value),
    );
    this.modeSaveBtn.addEventListener("click", () => {
      const name = this.modeProfileName.value.trim();
      if (!name) return;
      const profiles = loadModeProfiles();
      profiles[name] = { ...this.settings.mode };
      saveModeProfiles(profiles);
      this.modeProfileName.value = "";
      this.refreshModeProfiles();
      this.modeProfileSelect.value = name;
    });
    this.modeLoadBtn.addEventListener("click", () => {
      const name = this.modeProfileSelect.value;
      const saved = name ? loadModeProfiles()[name] : null;
      if (!saved) return;
      this.settings.mode = { ...saved };
      this.settings.modePreset = modePresetId(saved) ?? "custom";
      this.save();
      this.refreshModePreset();
      this.renderModes();
      this.refreshModeSync();
      this.cb.onGameModeLocal({ ...this.settings.mode });
    });
    this.modeDeleteBtn.addEventListener("click", () => {
      const name = this.modeProfileSelect.value;
      if (!name) return;
      const profiles = loadModeProfiles();
      delete profiles[name];
      saveModeProfiles(profiles);
      this.refreshModeProfiles();
    });
    this.modeResetBtn.addEventListener("click", () => {
      const id = this.settings.modePreset === "custom" ? "race" : this.settings.modePreset;
      this.applyModePreset(id);
    });
    this.modeVoteBtn.addEventListener("click", () =>
      this.cb.onVoteGameMode(
        { ...this.settings.mode },
        { ...this.settings.rules },
        modeLabel(this.settings.mode),
      ),
    );
    this.modePullBtn.addEventListener("click", () => {
      this.pullModePending = true;
      this.cb.onRequestGameMode();
    });
  }

  private applyModePreset(id: string) {
    if (id === "custom") return;
    this.settings.modePreset = id;
    this.settings.mode = presetMode(id);
    this.save();
    this.refreshModePreset();
    this.renderModes();
    this.refreshModeSync();
    this.cb.onGameModeLocal({ ...this.settings.mode });
  }

  /** Render the mode editor from the field schema. */
  renderModes() {
    if (!this.modesListEl) return;
    this.modesListEl.innerHTML = "";
    this.modeInputs.clear();
    const base = presetMode(this.settings.modePreset === "custom" ? "race" : this.settings.modePreset);
    for (const g of MODE_GROUPS) {
      const card = document.createElement("section");
      card.className = "settings-card settings-card--wide";
      card.dataset.ds = "card";
      card.dataset.dsKeep = "settings-card settings-card--wide";
      const body = document.createElement("div");
      body.className = "card-body";
      const title = document.createElement("h3");
      title.className = "card-title rule-group-title";
      const titleText = document.createElement("span");
      titleText.textContent = g.title;
      title.append(titleText);
      body.append(title);
      card.append(body);
      this.modesListEl.append(card);
      for (const f of g.fields) {
        const row = document.createElement("div");
        row.className = "rule-row";
        const label = document.createElement("label");
        label.textContent = f.unit ? `${f.label} (${f.unit})` : f.label;
        const cur = this.settings.mode[f.key];
        if (f.kind === "select") {
          const sel = document.createElement("select");
          for (const o of f.options ?? []) {
            const op = document.createElement("option");
            op.value = o.value;
            op.textContent = o.label;
            sel.append(op);
          }
          sel.value = String(cur);
          sel.dataset.setting = `mode.${String(f.key)}`;
          sel.addEventListener("change", () => {
            const v = typeof cur === "number" ? Number(sel.value) : sel.value;
            this.setModeField(f, v, true);
          });
          this.modeInputs.set(f.key, { select: sel, field: f });
          row.append(label, sel);
        } else if (f.kind === "toggle") {
          const input = document.createElement("input");
          input.type = "checkbox";
          input.dataset.setting = `mode.${String(f.key)}`;
          dsElement(input, "toggle", { color: "primary", size: "sm" });
          input.checked = Boolean(cur);
          input.addEventListener("change", () =>
            this.setModeField(f, input.checked, true),
          );
          this.modeInputs.set(f.key, { toggle: input, field: f });
          row.append(label, input);
        } else if (f.kind === "text") {
          const inp = document.createElement("input");
          inp.type = "text";
          inp.dataset.setting = `mode.${String(f.key)}`;
          dsElement(inp, "input", { size: "sm", keep: "w-full" });
          inp.value = String(cur);
          inp.addEventListener("change", () => this.setModeField(f, inp.value, true));
          this.modeInputs.set(f.key, { text: inp, field: f });
          row.append(label, inp);
        } else {
          const min = f.min ?? 0;
          const max = f.max ?? 1000;
          const step = f.step ?? 1;
          const range = document.createElement("input");
          range.type = "range";
          range.dataset.setting = `mode.${String(f.key)}`;
          dsElement(range, "range");
          range.min = String(min);
          range.max = String(max);
          range.step = String(step);
          const num = document.createElement("input");
          num.type = "number";
          num.dataset.setting = `mode.${String(f.key)}`;
          dsElement(num, "input", { size: "sm", keep: "w-20" });
          num.min = String(min);
          num.max = String(max);
          num.step = String(step);
          const reset = document.createElement("button");
          dsElement(reset, "button", { style: "ghost", size: "xs", shape: "square", keep: "rule-reset" });
          reset.textContent = "↺";
          reset.title = `reset to preset (${base[f.key]})`;
          const n = Number(cur);
          range.value = String(n);
          num.value = String(n);
          range.addEventListener("input", () => {
            num.value = range.value;
            this.setModeField(f, Number(range.value), false);
          });
          num.addEventListener("change", () =>
            this.setModeField(f, clampModeField(f, Number(num.value)), true),
          );
          reset.addEventListener("click", () => {
            const b = base[f.key];
            this.setModeField(f, typeof b === "number" ? b : n, true);
          });
          this.modeInputs.set(f.key, { range, num, field: f });
          row.append(label, range, num, reset);
        }
        body.append(row);
      }
    }
    refreshComponentStyles();
  }

  private setModeField(f: ModeField, value: string | number | boolean, rerender: boolean) {
    (this.settings.mode as unknown as Record<string, string | number | boolean>)[f.key] = value;
    const id = modePresetId(this.settings.mode);
    this.settings.modePreset = id ?? "custom";
    this.save();
    if (rerender) {
      this.refreshModePreset();
      this.renderModes();
    }
    this.refreshModeSync();
    this.cb.onGameModeLocal({ ...this.settings.mode });
  }

  /** Server reported its authoritative mode hash. */
  setServerModeHash(hash: number) {
    if (this.serverModeHash === hash) return;
    this.serverModeHash = hash;
    this.refreshModeSync();
  }

  /** Adopt the server's authoritative mode (or only remember it). */
  setServerMode(mode: GameMode, label?: string, hash?: number) {
    this.serverModeLabel = label ?? modeLabel(mode);
    if (hash !== undefined) this.serverModeHash = hash;
    if (this.pullModePending) {
      this.pullModePending = false;
      this.settings.mode = { ...mode };
      this.settings.modePreset = modePresetId(mode) ?? "custom";
      this.save();
      this.refreshModePreset();
      if (document.getElementById("page-modes")?.classList.contains("active")) this.renderModes();
      this.cb.onGameModeLocal({ ...mode });
    }
    this.refreshModeSync();
  }

  private refreshModeSync() {
    if (!this.modeSyncEl) return;
    if (this.serverModeHash === null) {
      this.modeSyncEl.textContent = "not connected — mode saved locally";
      this.modePullBtn.classList.add("hidden");
      return;
    }
    const local = hashMode(this.settings.mode);
    const inSync = local === this.serverModeHash;
    this.modeSyncEl.textContent = inSync
      ? `in sync · ${this.serverModeLabel || modeLabel(this.settings.mode)}`
      : "differs from server — vote or pull";
    this.modePullBtn.classList.toggle("hidden", inSync);
  }

  /** Populate the preset dropdown + cache the preset's baseline values. */
  refreshPreset() {
    if (!PRESETS.some((p) => p.id === this.settings.rulePreset)) {
      this.settings.rulePreset = PRESETS[0].id;
    }
    this.rulePresetSelect.innerHTML = "";
    for (const p of PRESETS) {
      const o = document.createElement("option");
      o.value = p.id;
      o.textContent = p.label;
      this.rulePresetSelect.append(o);
    }
    this.rulePresetSelect.value = this.settings.rulePreset;
    this.presetBaseMap = presetValues(this.settings.rulePreset);
    const note = presetNote(this.settings.rulePreset);
    this.rulePresetNoteEl.textContent = note;
    this.rulePresetNoteEl.style.display = note ? "block" : "none";
  }

  /** The default value of `key` for the currently chosen preset. */
  private presetBase(key: string): number {
    const v = this.presetBaseMap[key];
    return typeof v === "number" ? v : 0;
  }

  /** Repopulate the saved-profile dropdown. */
  refreshProfiles() {
    const profiles = loadProfiles();
    const keep = this.ruleProfileSelect.value;
    this.ruleProfileSelect.innerHTML = "";
    const none = document.createElement("option");
    none.value = "";
    none.textContent = "— profile —";
    this.ruleProfileSelect.append(none);
    for (const name of Object.keys(profiles).sort()) {
      const o = document.createElement("option");
      o.value = name;
      o.textContent = name;
      this.ruleProfileSelect.append(o);
    }
    if (keep && profiles[keep]) this.ruleProfileSelect.value = keep;
  }

  /** Render the rule categories + the selected/searched rule group. */
  renderRules() {
    if (!this.rulesListEl) return;
    this.rulesListEl.innerHTML = "";
    this.ruleInputs.clear();
    this.ruleGroupEls.clear();
    this.ruleBindButtons.clear();
    this.renderRuleCategories();

    const query = this.ruleSearch.trim();
    if (query) {
      const ruleDefs = SETTINGS.filter((def) => def.id.startsWith("rules."));
      const matches = new Set(
        searchSettings(ruleDefs, query).map((def) => def.id.slice("rules.".length)),
      );
      let any = false;
      for (const group of RULE_GROUPS) {
        const fields = group.fields.filter((field) => matches.has(field.key));
        if (fields.length === 0) continue;
        any = true;
        this.renderRuleGroup(group, fields);
      }
      if (!any) {
        const note = document.createElement("div");
        note.className = "weapon-note";
        note.textContent = "No rules match.";
        this.rulesListEl.append(note);
      }
    } else if (this.ruleCategory) {
      const group = RULE_GROUPS.find((g) => g.title === this.ruleCategory);
      if (group) this.renderRuleGroup(group, group.fields);
    } else {
      for (const group of RULE_GROUPS) this.renderRuleGroup(group, group.fields);
    }

    this.updateChangedStates();
    this.refreshToggles();
    this.refreshSync();
    refreshComponentStyles();
  }

  /** Sidebar categories for the Rules & tuning page. */
  private renderRuleCategories(): void {
    if (!this.rulesCategoriesEl) return;
    this.rulesCategoriesEl.innerHTML = "";
    const add = (label: string, count: number, key: string | null) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "rule-cat" + (!this.ruleSearch && this.ruleCategory === key ? " active" : "");
      const name = document.createElement("span");
      name.textContent = label;
      name.title = label;
      const num = document.createElement("small");
      num.textContent = String(count);
      btn.append(name, num);
      btn.addEventListener("click", () => {
        this.ruleCategory = key;
        this.ruleSearch = "";
        this.rulesSearchInput.value = "";
        this.renderRules();
      });
      this.rulesCategoriesEl.append(btn);
    };
    add("All rules", RULE_GROUPS.reduce((n, g) => n + g.fields.length, 0), null);
    for (const group of RULE_GROUPS) add(group.title, group.fields.length, group.title);
  }

  /** One rule group as a design-system card. `fields` may be a search subset. */
  private renderRuleGroup(group: RuleGroup, fields: RuleField[]): void {
    const card = document.createElement("section");
    card.className = "settings-card settings-card--wide";
    card.dataset.ds = "card";
    card.dataset.dsKeep = "settings-card settings-card--wide";
    const body = document.createElement("div");
    body.className = "card-body";
    const title = document.createElement("h3");
    // `rule-group-title` keeps the per-group "changed" highlight working.
    title.className = "card-title rule-group-title";
    if (group.icon) {
      const ic = document.createElement("span");
      ic.className = "rule-group-icon";
      ic.innerHTML =
        `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" ` +
        `stroke-linecap="round" stroke-linejoin="round"><path d="${group.icon}"/></svg>`;
      title.append(ic);
    }
    const titleText = document.createElement("span");
    titleText.textContent = group.title;
    title.append(titleText);
    body.append(title);
    card.append(body);
    this.rulesListEl.append(card);
    this.ruleGroupEls.set(group.title, title);

    // Weapon groups: an inline keybind (+ projectile colour) right under the name.
    if (group.bindAction) {
      const row = document.createElement("div");
      row.className = "rule-bind-row";
      const bl = document.createElement("span");
      bl.className = "bind-label";
      bl.textContent = "Key bind";
      const btn = document.createElement("button");
      btn.className = "bind-key";
      btn.dataset.action = group.bindAction;
      this.updateBindButton(btn, group.bindAction);
      btn.title = "click to bind · right-click (or Backspace) to clear";
      btn.addEventListener("click", () => this.startCapture(group.bindAction!));
      btn.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clearBind(group.bindAction!);
        this.renderBinds();
      });
      row.append(bl, btn);
      // Projectile / pellet colour picker for this weapon.
      const weaponId = WEAPON_ID[group.id ?? ""] ?? 0;
      if (weaponId > 0) {
        const cl = document.createElement("span");
        cl.className = "bind-label";
        cl.textContent = "Color";
        const ci = document.createElement("input");
        ci.type = "color";
        ci.className = "weapon-color";
        const hex =
          this.settings.projectileColors[String(weaponId)] ??
          this.cb.getProjectileColor(weaponId);
        ci.value = hex;
        ci.title = "Projectile / pellet colour";
        ci.addEventListener("input", () => {
          this.settings.projectileColors[String(weaponId)] = ci.value;
          this.save();
          this.cb.onProjectileColor(weaponId, ci.value);
        });
        this.cb.onProjectileColor(weaponId, hex); // seed the client from the saved colour
        row.append(cl, ci);
        // Opacity (0..1) — e.g. make the ball launcher's orbs translucent.
        const al = document.createElement("span");
        al.className = "bind-label";
        al.textContent = "Opacity";
        const ai = document.createElement("input");
        ai.type = "range";
        ai.min = "0";
        ai.max = "1";
        ai.step = "0.05";
        ai.className = "weapon-alpha";
        const a0 =
          this.settings.projectileAlpha[String(weaponId)] ??
          this.cb.getProjectileAlpha(weaponId);
        ai.value = String(a0);
        ai.title = "Projectile / orb opacity";
        dsElement(ai, "range", { keep: "w-24" });
        ai.addEventListener("input", () => {
          const v = Number(ai.value);
          this.settings.projectileAlpha[String(weaponId)] = v;
          this.save();
          this.cb.onProjectileAlpha(weaponId, v);
        });
        this.cb.onProjectileAlpha(weaponId, a0); // seed the client
        row.append(al, ai);
      }
      body.append(row);
      this.ruleBindButtons.set(group.bindAction, btn);
    }
    for (const f of fields) {
      const row = document.createElement("div");
      row.className = "rule-row";
      const label = document.createElement("label");
      label.textContent = f.unit ? `${f.label} (${f.unit})` : f.label;
      const range = document.createElement("input");
      range.type = "range";
      range.min = String(f.min);
      range.max = String(f.max);
      range.step = String(f.step);
      range.dataset.rule = f.key;
      range.dataset.setting = `rules.${f.key}`;
      dsElement(range, "range");
      const num = document.createElement("input");
      num.type = "number";
      num.min = String(f.min);
      num.max = String(f.max);
      num.step = String(f.step);
      num.dataset.rule = f.key;
      num.dataset.setting = `rules.${f.key}`;
      dsElement(num, "input", { size: "sm", keep: "w-20" });
      const reset = document.createElement("button");
      reset.className = "rule-reset";
      reset.textContent = "↺";
      reset.dataset.rule = f.key;
      reset.title = `reset to preset (${this.presetBase(f.key)})`;
      dsElement(reset, "button", { style: "ghost", size: "xs", shape: "square", keep: "rule-reset" });
      const cur = this.ruleValue(f);
      range.value = String(cur);
      num.value = String(cur);
      range.addEventListener("input", () => this.setRule(f, Number(range.value)));
      num.addEventListener("change", () => this.setRule(f, Number(num.value)));
      reset.addEventListener("click", () => this.setRule(f, this.presetBase(f.key)));
      this.ruleInputs.set(f.key, { range, num, reset, field: f });
      row.append(label, range, num, reset);
      body.append(row);
    }
  }

  /** Apply a group of rule changes at once (server + local prediction). */
  private applyRules(partial: Record<string, number>) {
    Object.assign(this.settings.rules, partial);
    this.save();
    this.renderRules();
    this.cb.onRules({ ...this.settings.rules });
  }

  /** Reflect the dash/walljump toggle buttons from the current values. */
  private refreshToggles() {
    const dash = dashEnabled(this.settings.rules);
    if (this.ruleDashBtn.checked !== dash) this.ruleDashBtn.checked = dash;
    const wj = walljumpEnabled(this.settings.rules);
    if (this.ruleWjBtn.checked !== wj) this.ruleWjBtn.checked = wj;
  }

  /** The server reported its authoritative ruleset hash (from a snapshot). */
  setServerRulesHash(hash: number) {
    if (this.serverRulesHash === hash) return;
    this.serverRulesHash = hash;
    this.refreshSync();
  }

  /** Remember the server's authoritative ruleset without overwriting local. */
  setServerRules(rules: Record<string, number>, hash?: number) {
    if (hash !== undefined) this.serverRulesHash = hash;
    if (this.pullRulesPending) {
      this.pullRulesPending = false;
      // Merge over local so keys the server omits keep their values.
      this.settings.rules = { ...this.settings.rules, ...rules };
      this.save();
      this.renderRules();
      this.cb.onRulesLocal({ ...this.settings.rules });
    }
    this.refreshSync();
  }

  /** Compare local rules to the server's authoritative ruleset hash. */
  private refreshSync() {
    if (!this.ruleSyncEl) return;
    if (this.serverRulesHash === null) {
      this.ruleSyncEl.textContent = "not connected — rules saved locally";
      this.ruleSyncEl.className = "rule-sync";
      this.rulePullBtn.classList.add("hidden");
      return;
    }
    const local = hashRules(this.settings.rules);
    const inSync = local === this.serverRulesHash;
    this.ruleSyncEl.textContent = inSync
      ? `server rules ${local.toString(16)} · in sync`
      : `server ${this.serverRulesHash.toString(16)} ≠ local ${local.toString(16)}`;
    this.ruleSyncEl.className = inSync ? "rule-sync ok" : "rule-sync warn";
    this.rulePullBtn.classList.toggle("hidden", inSync);
  }

  /** Flag fields whose value differs from the preset default (per-field reset). */
  private updateChangedStates() {
    for (const g of RULE_GROUPS) {
      let anyChanged = false;
      for (const f of g.fields) {
        const base = this.presetBase(f.key);
        const changed = this.ruleValue(f) !== base;
        if (changed) anyChanged = true;
        const inputs = this.ruleInputs.get(f.key);
        if (inputs) {
          // `data-changed`, not a class: the daisyUI class pass rewrites
          // `className` on `[data-ds]` elements.
          if (changed) inputs.reset.dataset.changed = "1";
          else delete inputs.reset.dataset.changed;
          inputs.reset.title = changed ? `changed — reset to ${base}` : `default (${base})`;
        }
      }
      this.ruleGroupEls.get(g.title)?.classList.toggle("changed", anyChanged);
    }
  }

  private ruleValue(f: RuleField): number {
    const v = this.settings.rules[f.key];
    return typeof v === "number" && Number.isFinite(v) ? v : f.def;
  }

  private setRule(f: RuleField, raw: number) {
    if (!Number.isFinite(raw)) return;
    const value = clampRule(f, raw);
    this.settings.rules[f.key] = value;
    const inputs = this.ruleInputs.get(f.key);
    if (inputs) {
      if (Number(inputs.range.value) !== value) inputs.range.value = String(value);
      if (Number(inputs.num.value) !== value) inputs.num.value = String(value);
    }
    this.save();
    this.cb.onRule(f.key, value);
    this.updateChangedStates();
  }

  /** Wire the share-config copy/apply buttons. */
  private bindConfigButtons() {
    const shareBtn = document.getElementById("share-config");
    const codeInput = document.getElementById("config-code") as HTMLInputElement;
    const applyBtn = document.getElementById("apply-config");
    shareBtn?.addEventListener("click", () => {
      const code = encodeConfig(this.settings);
      if (!code) return;
      try {
        void navigator.clipboard.writeText(code);
        shareBtn!.textContent = "copied!";
        setTimeout(() => (shareBtn!.textContent = "Copy share code"), 1500);
      } catch {
        codeInput.value = code;
      }
    });
    applyBtn?.addEventListener("click", () => {
      const decoded = decodeConfig(codeInput.value.trim());
      if (!decoded) {
        codeInput.value = "";
        codeInput.placeholder = "invalid code";
        return;
      }
      // decodeConfig validates through the same schema/migration path as a
      // localStorage load, so this is a complete, safe Settings object.
      this.settings = decoded;
      this.store.replace(this.settings, false);
      this.applyAll();
      codeInput.value = "";
    });
    document.getElementById("settings-reset-all")?.addEventListener("click", () => {
      if (!confirm("Reset every setting to its default and clear all personal overrides?")) return;
      clearAllExplicit();
      applyGlobalDefaults(this.settings);
      this.settings.projectileColors = {};
      this.settings.projectileAlpha = {};
      this.save();
      this.cb.onDebugReset();
      this.applyAll();
      this.refreshDebug();
    });
  }

  private bind() {
    // FOV
    this.fovSlider.addEventListener("input", () => this.setFov(Number(this.fovSlider.value)));
    this.fovInput.addEventListener("input", () => {
      const v = Number(this.fovInput.value);
      if (Number.isFinite(v)) this.setFov(v);
    });

    // Sensitivity (the converter under Mouse is the only sensitivity editor).

    // Texture filtering quality
    this.textureModeSelect.addEventListener("change", () =>
      this.setTextureMode(this.textureModeSelect.value as TextureMode),
    );
    // Panini warp strength (0 = off)
    this.paniniSlider.addEventListener("input", () =>
      this.setPanini(Number(this.paniniSlider.value)),
    );
    this.paniniInput.addEventListener("input", () => {
      const v = Number(this.paniniInput.value);
      if (Number.isFinite(v)) this.setPanini(v);
    });

    // HUD layout editor + spectate option.
    this.openHudEditorBtn.addEventListener("click", () => {
      this.setOpen(false);
      this.hudEditor.openEditor();
    });
    this.resetHudLayoutBtn.addEventListener("click", () => {
      this.settings.hudLayout = resetHudStore(this.settings.hudLayout);
      this.hudEditor.setStore(this.settings.hudLayout);
      this.save();
      this.cb.onHudLayout(this.settings.hudLayout);
    });
    this.hudSpectatePlayer.addEventListener("change", () => {
      this.settings.hudLayout.spectateUsePlayer = this.hudSpectatePlayer.checked;
      this.save();
      this.cb.onHudLayout(this.settings.hudLayout);
    });

    // Master sound volume
    this.volumeSlider.addEventListener("input", () => this.setVolume(Number(this.volumeSlider.value)));

    // cg_truelightning (LG beam anchored to the crosshair)
    this.trueLightningSlider.addEventListener("input", () =>
      this.setTrueLightning(Number(this.trueLightningSlider.value)),
    );

    // Player trail (neon ribbon): duration + width + glow + self/enemy colours.
    const trailInputs: (HTMLInputElement | HTMLSelectElement)[] = [
      this.trailSlider,
      this.trailWidthSlider,
      this.trailGlowSlider,
      this.trailSelfModeSelect,
      this.trailSelfColorInput,
      this.trailSelfTeamColorInput,
      this.trailEnemyModeSelect,
      this.trailEnemyColorInput,
      this.trailEnemyTeamColorInput,
    ];
    for (const el of trailInputs) {
      el.addEventListener(el instanceof HTMLSelectElement ? "change" : "input", () =>
        this.setTrail(),
      );
    }
    this.wireFeedback();

    // Nickname (saved to the leaderboard identity + pushed to the game server).
    const saveNick = () => void this.saveNickname();
    this.nickSaveBtn.addEventListener("click", saveNick);
    this.nickInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveNick();
      }
    });

    // Key binds: restore the shipped default layout.
    this.bindsResetBtn.addEventListener("click", () => {
      if (!window.confirm("Reset every key bind to the defaults?")) return;
      this.settings.binds = { ...DEFAULT_BINDS };
      this.save();
      this.cb.onBinds({ ...this.settings.binds });
      this.renderBinds();
    });
  }

  /** Sync the nickname field with the stored identity. */
  refreshNickname() {
    const id = getIdentity();
    if (id) this.nickInput.value = id.nickname;
  }

  /** Register (first time) or rename (existing identity) the nickname. */
  private async saveNickname() {
    const nickname = this.nickInput.value.trim();
    if (!nickname) {
      this.nickStatusEl.textContent = "enter a nickname";
      return;
    }
    const id = getIdentity();
    this.nickSaveBtn.disabled = true;
    this.nickStatusEl.textContent = "saving…";
    try {
      const saved = await registerNickname(nickname, id?.token);
      this.nickInput.value = saved.nickname;
      this.nickStatusEl.textContent = "saved";
      this.cb.onNickname(saved.nickname);
      window.setTimeout(() => {
        if (this.nickStatusEl.textContent === "saved") this.nickStatusEl.textContent = "";
      }, 2000);
    } catch (e) {
      this.nickStatusEl.textContent = (e as Error).message;
    } finally {
      this.nickSaveBtn.disabled = false;
    }
  }

  private setFov(v: number) {
    this.settings.fov = v;
    this.fovValue.textContent = `${v}°`;
    if (Number(this.fovInput.value) !== v) this.fovInput.value = String(v);
    if (this.fovSlider.value !== String(v)) this.fovSlider.value = String(v);
    this.cb.onFov(v);
    this.save();
    // No re-render: the weapon picker's FOV slider is live and re-rendering
    // would drop the drag. Inherited values update in place.
    this.weaponPanel?.setGlobals(this.weaponGlobals());
  }

  private setPanini(v: number, persist = true) {
    const p = Math.min(3, Math.max(0, Number.isFinite(v) ? v : 0));
    this.settings.panini = p;
    this.paniniValue.textContent = p === 0 ? "Off" : p.toFixed(2);
    if (Number(this.paniniInput.value) !== p) this.paniniInput.value = String(p);
    if (Number(this.paniniSlider.value) !== p) this.paniniSlider.value = String(p);
    this.cb.onPanini(p);
    if (persist) this.save();
  }

  private setSensitivity(v: number) {
    this.settings.sensitivity = v;
    // Belt and braces: a leftover legacy shared-default must never beat the
    // value the player just applied. (normalize/merge drop it on load too.)
    if (this.settings.weaponSettings.defaults.sensitivity !== undefined) {
      delete this.settings.weaponSettings.defaults.sensitivity;
      this.cb.onWeaponSettings(this.settings.weaponSettings);
    }
    this.cb.onSensitivity(v);
    this.save();
    this.weaponPanel?.setGlobals(this.weaponGlobals());
  }

  /** A detached copy so the client can't observe later menu edits. */
  private hudSnapshot(): HudSettings {
    const h = this.settings.hud;
    return {
      cgazStyle: {
        ...h.cgazStyle,
        colors: { ...h.cgazStyle.colors },
        alphas: { ...h.cgazStyle.alphas },
      },
    };
  }

  private setTextureMode(mode: TextureMode) {
    this.settings.textureMode = mode;
    this.cb.onTextureMode(mode);
    this.save();
  }

  private setVolume(v: number) {
    const clamped = Math.min(1, Math.max(0, v));
    this.settings.volume = clamped;
    this.volumeValue.textContent = `${Math.round(clamped * 100)}%`;
    if (Number(this.volumeSlider.value) !== clamped) this.volumeSlider.value = String(clamped);
    this.cb.onVolume(clamped);
    this.save();
  }

  private setTrueLightning(v: number) {
    const clamped = Math.min(1, Math.max(0, v));
    this.settings.cgTrueLightning = clamped;
    this.trueLightningValue.textContent = clamped.toFixed(2);
    if (Number(this.trueLightningSlider.value) !== clamped) {
      this.trueLightningSlider.value = String(clamped);
    }
    this.cb.onTrueLightning(clamped);
    this.save();
  }

  /** Persist + apply the player-trail settings, keeping the controls in sync. */
  private setTrail(): void {
    const mode = (v: string): TrailColorMode =>
      v === "random" || v === "team" ? v : "custom";
    const cfg: TrailConfig = {
      seconds: Math.min(3, Math.max(0, Number(this.trailSlider.value))),
      width: Math.min(8, Math.max(0.5, Number(this.trailWidthSlider.value))),
      intensity: Math.min(2, Math.max(0.1, Number(this.trailGlowSlider.value))),
      selfMode: mode(this.trailSelfModeSelect.value),
      selfColor: this.trailSelfColorInput.value,
      selfTeamColor: this.trailSelfTeamColorInput.value,
      enemyMode: mode(this.trailEnemyModeSelect.value),
      enemyColor: this.trailEnemyColorInput.value,
      enemyTeamColor: this.trailEnemyTeamColorInput.value,
    };
    this.settings.trailSeconds = cfg.seconds;
    this.settings.trailWidth = cfg.width;
    this.settings.trailIntensity = cfg.intensity;
    this.settings.trailSelfMode = cfg.selfMode;
    this.settings.trailSelfColor = cfg.selfColor;
    this.settings.trailSelfTeamColor = cfg.selfTeamColor;
    this.settings.trailEnemyMode = cfg.enemyMode;
    this.settings.trailEnemyColor = cfg.enemyColor;
    this.settings.trailEnemyTeamColor = cfg.enemyTeamColor;
    this.syncTrailUi(cfg);
    this.cb.onTrail(cfg);
    this.save();
  }

  /** Reflect a trail state in the duration / width / glow / colour controls. */
  private syncTrailUi(cfg: TrailConfig): void {
    if (Number(this.trailSlider.value) !== cfg.seconds) {
      this.trailSlider.value = String(cfg.seconds);
    }
    // 0 shows a compact "off" instead of 0.0 s.
    this.trailValue.textContent = cfg.seconds <= 0 ? "off" : `${cfg.seconds.toFixed(1)} s`;
    if (Number(this.trailWidthSlider.value) !== cfg.width) {
      this.trailWidthSlider.value = String(cfg.width);
    }
    this.trailWidthValue.textContent = cfg.width.toFixed(1);
    if (Number(this.trailGlowSlider.value) !== cfg.intensity) {
      this.trailGlowSlider.value = String(cfg.intensity);
    }
    this.trailGlowValue.textContent = cfg.intensity.toFixed(2);
    if (this.trailSelfModeSelect.value !== cfg.selfMode) {
      this.trailSelfModeSelect.value = cfg.selfMode;
    }
    if (this.trailSelfColorInput.value !== cfg.selfColor) {
      this.trailSelfColorInput.value = cfg.selfColor;
    }
    if (this.trailSelfTeamColorInput.value !== cfg.selfTeamColor) {
      this.trailSelfTeamColorInput.value = cfg.selfTeamColor;
    }
    if (this.trailEnemyModeSelect.value !== cfg.enemyMode) {
      this.trailEnemyModeSelect.value = cfg.enemyMode;
    }
    if (this.trailEnemyColorInput.value !== cfg.enemyColor) {
      this.trailEnemyColorInput.value = cfg.enemyColor;
    }
    if (this.trailEnemyTeamColorInput.value !== cfg.enemyTeamColor) {
      this.trailEnemyTeamColorInput.value = cfg.enemyTeamColor;
    }
  }

  /** Wire the live tuning controls (Video / Network / Advanced). */
  private bindDebug() {
    this.dbgToggles = Array.from(
      this.menuEl.querySelectorAll<HTMLInputElement>("[data-dbg-toggle]"),
    );
    for (const t of this.dbgToggles) {
      t.addEventListener("change", () => {
        const key = t.dataset.dbgToggle ?? "";
        this.cb.onDebug(key, t.checked);
        this.refreshDebug();
      });
    }
    for (const d of TUNING_SLIDERS) {
      const list = this.menuEl.querySelector<HTMLElement>(`#${d.group}`);
      if (!list) continue;
      const row = document.createElement("div");
      row.className = "rule-row";
      const label = document.createElement("label");
      label.textContent = d.label;
      const range = document.createElement("input");
      range.type = "range";
      range.min = String(d.min);
      range.max = String(d.max);
      range.step = String(d.step);
      if (d.tip) range.title = d.tip;
      range.dataset.setting = `advanced.debug.${d.key}`;
      dsElement(range, "range", { keep: "w-full" });
      const value = document.createElement("span");
      value.className = "rule-sync";
      row.append(label, range, value);
      list.appendChild(row);
      range.addEventListener("input", () => {
        const v = Number(range.value);
        value.textContent = formatDebug(d, v);
        this.cb.onDebug(d.key, v);
      });
      this.dbgSliders.set(d.key, { range, value });
    }
    this.dbgServerInput = this.menuEl.querySelector<HTMLInputElement>("#dbg-server");
    this.dbgMapInput = this.menuEl.querySelector<HTMLInputElement>("#dbg-map");
    const reload = this.menuEl.querySelector<HTMLButtonElement>("#dbg-reload");
    reload?.addEventListener("click", () =>
      this.cb.onDebugReload(
        this.dbgServerInput?.value.trim() ?? "",
        this.dbgMapInput?.value.trim() ?? "",
      ),
    );
    this.menuEl
      .querySelector<HTMLButtonElement>("#dbg-reset")
      ?.addEventListener("click", () => {
        this.cb.onDebugReset();
        // These live in Video/Network but are persisted, so reset them here too.
        this.setTrueLightning(1);
        this.settings.downstreamHz = 125;
        this.save();
        this.syncDownstream(125);
      });
  }

  /**
   * Wire the downstream-snapshot picker (Network tab, persisted).
   * The value is the client's emulated snapshot rate in Hz (see DOWNSTREAM_RATES).
   */
  private bindDownstream() {
    this.downstreamSelects = Array.from(
      this.menuEl.querySelectorAll<HTMLSelectElement>("select[data-downstream]"),
    );
    for (const sel of this.downstreamSelects) {
      sel.innerHTML = "";
      for (const hz of DOWNSTREAM_RATES) {
        const o = document.createElement("option");
        o.value = String(hz);
        o.textContent =
          hz === 125
            ? "125 Hz (default)"
            : hz === 62.5
              ? "62.5 Hz (low bandwidth)"
              : `${hz} Hz`;
        sel.appendChild(o);
      }
      sel.addEventListener("change", () => this.setDownstream(Number(sel.value)));
    }
    // Seed the client from the saved setting; a `?downstream=`/`?snap=` URL param
    // is parsed later in main and overrides this for the session.
    this.cb.onDebug("downstream", this.settings.downstreamHz);
    this.syncDownstream(this.settings.downstreamHz);
  }

  /** Wire the Video-tab VFX colour pickers (persisted + applied live). */
  private bindVfxColors() {
    for (const el of Array.from(
      this.menuEl.querySelectorAll<HTMLInputElement>("input[data-vfx-color]"),
    )) {
      const key = (el.dataset.vfxColor ?? "") as keyof Settings["vfxColors"];
      const hex = this.settings.vfxColors[key] ?? "#ffffff";
      this.cb.onVfxColor(key, hex); // seed the client from the saved colour
      el.value = hex;
      el.title = "VFX colour";
      el.addEventListener("input", () => {
        this.settings.vfxColors[key] = el.value;
        this.save();
        this.cb.onVfxColor(key, el.value);
      });
    }
  }

  /** Sync both pickers to `hz` (a no-op when already there). */
  private syncDownstream(hz: number) {
    const v = String(hz);
    for (const sel of this.downstreamSelects) if (sel.value !== v) sel.value = v;
  }

  /** Persist + apply a new downstream snapshot rate. */
  private setDownstream(hz: number) {
    this.settings.downstreamHz = hz;
    this.save();
    this.cb.onDebug("downstream", hz);
    this.syncDownstream(hz);
  }

  /** Sync the live tuning controls with the current state. */
  refreshDebug() {
    const d = this.cb.getDebug();
    for (const t of this.dbgToggles) {
      const on = Boolean(d[t.dataset.dbgToggle ?? ""]);
      if (t.checked !== on) t.checked = on;
    }
    for (const s of TUNING_SLIDERS) {
      const ref = this.dbgSliders.get(s.key);
      if (!ref) continue;
      const v = Number(d[s.key] ?? 0);
      if (Number(ref.range.value) !== v) ref.range.value = String(v);
      ref.value.textContent = formatDebug(s, v);
    }
    // True lightning is a persisted slider but can be pinned by the URL.
    const tl = Number(d.truelightning ?? this.settings.cgTrueLightning);
    if (Number(this.trueLightningSlider.value) !== tl) {
      this.trueLightningSlider.value = String(tl);
      this.trueLightningValue.textContent = tl.toFixed(2);
    }
    // Reflect the live rate (may be pinned by `?downstream=`/`?snap=`).
    this.syncDownstream(Number(d.downstream ?? this.settings.downstreamHz));
  }

  /** Wire the generic enemy-visual / hit-feedback controls. */
  private wireFeedback(): void {
    const els = this.menuEl.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-fb]");
    for (const el of els) {
      const changeOnly =
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "color"));
      el.addEventListener(changeOnly ? "change" : "input", () => {
        const group = el.dataset.fb;
        const key = el.dataset.fbKey;
        if (!group || !key) return;
        const target =
          group === "enemyVisual"
            ? this.settings.enemyVisual
            : group === "hitFeedback"
              ? this.settings.hitFeedback
              : null;
        if (!target) return;
        (target as unknown as Record<string, unknown>)[key] = feedbackValue(el);
        this.syncFeedbackUi(el);
        this.save();
        this.cb.onFeedback?.();
      });
    }
  }

  private syncFeedback(): void {
    const els = this.menuEl.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-fb]");
    for (const el of els) this.syncFeedbackUi(el);
  }

  private syncFeedbackUi(el: HTMLInputElement | HTMLSelectElement): void {
    const group = el.dataset.fb;
    const key = el.dataset.fbKey;
    if (!group || !key) return;
    const source =
      group === "enemyVisual"
        ? this.settings.enemyVisual
        : group === "hitFeedback"
          ? this.settings.hitFeedback
          : null;
    if (!source) return;
    const value = (source as unknown as Record<string, unknown>)[key];
    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox") el.checked = value === true;
      else el.value = String(value);
    } else {
      el.value = String(value);
    }
    const span = document.getElementById(`${el.id}-value`);
    if (span) span.textContent = feedbackText(el.id, value);
  }

  private save() {
    // The menu has already applied its change to the controls/callbacks; the
    // store owns persistence and does not need to re-run applyAll().
    this.store.commit(false);
  }

  /**
   * Add reset controls + visibility for every registered setting in the menu.
   *
   * Idempotent: `applyAll()` runs after every commit, and dynamic pages rebuild
   * their rows. Controls are found through `data-setting` or the static
   * selector bridge. Group settings keep their existing editors and are only
   * listed/reset from the admin defaults page.
   */
  private installSettingResets(): void {
    const role = this.cb.getRole();
    const doc = loadCachedGlobalDefaults();
    for (const def of SETTINGS) {
      const visible = canSeeSetting(accessFor(def, doc), role);
      if (def.kind === "group") {
        for (const selector of GROUP_CONTAINERS[def.id] ?? []) {
          const target = this.menuEl.querySelector<HTMLElement>(selector);
          const container = target?.closest<HTMLElement>(".settings-card") ?? target;
          container?.toggleAttribute("data-setting-hidden", !visible);
        }
        continue;
      }
      if (!def.path) continue;
      const controls = this.settingControls(def);
      if (controls.length === 0) continue;
      this.applySettingVisibility(controls, visible);
      if (!visible) continue;
      this.attachSettingReset(def, controls);
    }
  }

  private settingControlsById(id: string): HTMLElement[] {
    const def = SETTINGS.find((d) => d.id === id);
    return def ? this.settingControls(def) : [];
  }

  private settingControls(def: SettingDef): HTMLElement[] {
    const found = new Set<HTMLElement>();
    for (const el of Array.from(this.menuEl.querySelectorAll<HTMLElement>(`[data-setting="${def.id}"]`))) {
      found.add(el);
    }
    for (const selector of SETTING_CONTROLS[def.id] ?? []) {
      for (const el of Array.from(this.menuEl.querySelectorAll<HTMLElement>(selector))) found.add(el);
    }
    return [...found];
  }

  /** Hide the smallest row/group that owns this setting, or the control itself. */
  private applySettingVisibility(controls: HTMLElement[], visible: boolean): void {
    const containers = new Set<HTMLElement>();
    for (const control of controls) {
      if (control.parentElement?.classList.contains("rule-toggles")) {
        containers.add(control);
        const prev = control.previousElementSibling;
        if (prev instanceof HTMLElement) containers.add(prev);
        continue;
      }
      const container =
        control.closest<HTMLElement>(".menu-group") ??
        control.closest<HTMLElement>(".rule-row") ??
        control.closest<HTMLElement>(".hud-ed-field") ??
        control.closest<HTMLElement>(".cgaz-style-row") ??
        control;
      containers.add(container);
    }
    for (const container of containers) {
      container.toggleAttribute("data-setting-hidden", !visible);
    }
  }

  private attachSettingReset(def: SettingDef, controls: HTMLElement[]): void {
    // Mark explicit on any user edit of this setting.
    for (const c of controls) {
      if (c.dataset.settingBound) continue;
      c.dataset.settingBound = "1";
      if (def.help) {
        const tip = `${def.help} (default ${formatDefault(def)})`;
        c.title = c.title ? `${c.title}\n${tip}` : tip;
      }
      const mark = () => {
        markExplicit(def.id);
        this.refreshSettingResetState(def);
      };
      c.addEventListener("input", mark);
      c.addEventListener("change", mark);
    }

    // One reset per row/container. The same setting can appear on more than one
    // page (e.g. FOV in Video and in the sensitivity converter), and a slider +
    // number pair share one row.
    const groups = new Map<HTMLElement, HTMLElement[]>();
    for (const control of controls) {
      if (control.parentElement?.classList.contains("rule-toggles")) {
        groups.set(control, [control]);
        continue;
      }
      const key =
        control.closest<HTMLElement>(
          ".menu-group, .rule-row, .hud-ed-field, .cgaz-style-row, .cgaz-zone-row, .row, .sound-row, .maps-vote, .config, .rules-profiles, .lb-controls, label",
        ) ?? control;
      const list = groups.get(key) ?? [];
      list.push(control);
      groups.set(key, list);
    }

    if (def.inlineReset === false) {
      this.refreshSettingResetState(def);
      return;
    }

    for (const [container, list] of groups.entries()) {
      if (container.querySelector(".rule-reset")) continue;
      const control = list[list.length - 1];
      let anchor = control;
      if (
        control instanceof HTMLSelectElement &&
        control.nextElementSibling instanceof HTMLElement &&
        control.nextElementSibling.classList.contains("cycle-select")
      ) {
        anchor = control.nextElementSibling;
      }
      if (!anchor || anchor.dataset.settingReset === def.id) continue;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = "\u21ba";
      btn.title = `reset ${def.label} to default (${formatDefault(def)})`;
      btn.dataset.resetFor = def.id;
      dsElement(btn, "button", { style: "ghost", size: "xs", shape: "square", keep: "setting-reset" });
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (def.runtimeKey) {
          // Runtime flags have no Settings path: drive the control, then clear
          // the explicit marker so the default keeps following the admin value.
          for (const c of controls) this.setControlValue(c, effectiveValue(def));
          const first = controls[0];
          if (first) {
            first.dispatchEvent(
              new Event(
                first instanceof HTMLInputElement && first.type === "checkbox" ? "change" : "input",
                { bubbles: true },
              ),
            );
          }
          clearExplicit(def.id);
        } else {
          resetSetting(this.settings, def, loadCachedGlobalDefaults());
          this.save();
          this.applyAll();
        }
      });

      const parent = anchor.parentElement;
      if (parent && this.resetAsSibling(parent)) {
        parent.insertBefore(btn, anchor.nextSibling);
      } else {
        const wrap = document.createElement("span");
        const grow = anchor.matches(
          'input[type="range"], input[type="number"], input[type="text"], select, .cycle-select',
        );
        wrap.className = "setting-reset-wrap" + (grow ? " setting-reset-wrap--grow" : "");
        parent?.insertBefore(wrap, anchor);
        wrap.append(anchor, btn);
      }
      anchor.dataset.settingReset = def.id;
    }
    this.refreshSettingResetState(def);
  }

  /** Can the reset button sit as a sibling without breaking the row layout? */
  private resetAsSibling(parent: HTMLElement): boolean {
    return [
      "row",
      "rule-toggles",
      "rules-profiles",
      "config",
      "maps-vote",
      "lb-controls",
      "sound-row",
      "hud-ed-range",
    ].some((cls) => parent.classList.contains(cls));
  }

  private setControlValue(control: HTMLElement, value: unknown): void {
    if (control instanceof HTMLInputElement) {
      if (control.type === "checkbox" || control.type === "radio") control.checked = Boolean(value);
      else control.value = String(value ?? "");
      return;
    }
    if (control instanceof HTMLSelectElement) control.value = String(value ?? "");
  }

  private refreshSettingResetState(def: SettingDef, controls = this.settingControls(def)): void {
    const changed = !atDefault(this.settings, def, loadCachedGlobalDefaults());
    for (const btn of Array.from(
      this.menuEl.querySelectorAll<HTMLElement>(`[data-reset-for="${def.id}"]`),
    )) {
      btn.toggleAttribute("data-changed", changed);
    }
    // Keep the native control in sync for runtime settings reset via the button
    // (the existing live debug handler already updated the game state).
    void controls;
  }

  /** Global values the weapon profiles inherit from. */
  private weaponGlobals() {
    return {
      sensitivity: this.settings.sensitivity,
      fov: this.settings.fov,
      hitSounds: true,
      // The slot selected in the crosshair editor is the one in use. Per-weapon
      // overrides still win; `defaultId` is only the fallback after a deletion.
      crosshair: this.settings.crosshair.activeId || this.settings.crosshair.defaultId,
    };
  }

  /** Drop weapon-profile references to crosshair slots that no longer exist. */
  private sanitizeWeaponSlots(store: CrosshairStore) {
    const ids = new Set(store.slots.map((s) => s.id));
    const fix = (p: WeaponSettings["defaults"]) => {
      if (p.crosshair && !ids.has(p.crosshair)) delete p.crosshair;
      if (p.zoomCrosshair && !ids.has(p.zoomCrosshair)) delete p.zoomCrosshair;
    };
    fix(this.settings.weaponSettings.defaults);
    for (const p of Object.values(this.settings.weaponSettings.overrides)) fix(p);
  }

  private slotRefs() {
    return this.settings.crosshair.slots.map((s) => ({ id: s.id, name: s.name }));
  }

  /**
   * Push slot/global changes into the weapon panel. Re-rendering on every
   * crosshair slider tick would steal focus, so the slot signature guard keeps
   * it to actual slot changes (pass `force` for global value changes).
   */
  private syncWeaponPanel(force = false) {
    const sig = JSON.stringify(this.slotRefs());
    if (!force && sig === this.lastSlotSig) return;
    this.lastSlotSig = sig;
    this.weaponPanel?.set(
      this.settings.weaponSettings,
      this.weaponGlobals(),
      this.slotRefs(),
    );
  }

  /** Refresh the admin panel (called after the server settings arrive). */
  refreshAdmin(): void {
    this.adminPanel.refresh();
  }

  /** Sync all UI + game state from the stored settings. */
  /** Open the page/section that owns a setting and highlight its control. */
  private navigateToSetting(def: SettingDef): void {
    if (def.id === "controls.sensitivity" || def.id.startsWith("weapons.defaults.")) {
      this.weaponPanel.showTab("defaults");
    }
    // Rules are rendered one category at a time; select the owning group so
    // the target row actually exists after navigation.
    if (def.id.startsWith("rules.")) {
      const key = def.id.slice("rules.".length);
      const group = RULE_GROUPS.find((g) => g.fields.some((f) => f.key === key));
      if (group) {
        this.ruleCategory = group.title;
        this.ruleSearch = "";
        if (this.rulesSearchInput) this.rulesSearchInput.value = "";
      }
    }
    const page = pageForSetting(def);
    this.showPage(page);
    this.refreshSettingsAccess();

    let target: HTMLElement | null = this.settingControlsById(def.id)[0] ?? null;
    if (!target && def.id.startsWith("sounds.")) {
      target = this.menuEl.querySelector<HTMLElement>("#sound-list");
    }
    if (!target && def.id.startsWith("binds.")) {
      target = this.menuEl.querySelector<HTMLElement>("#binds");
    }
    if (!target) {
      for (const selector of GROUP_CONTAINERS[def.id] ?? []) {
        target = this.menuEl.querySelector<HTMLElement>(selector);
        if (target) break;
      }
    }
    if (!target) {
      target = this.menuEl.querySelector<HTMLElement>("#page-" + page);
    }
    if (!target) return;
    const container =
      target.closest<HTMLElement>(
        ".menu-group, .rule-row, .hud-ed-field, .cgaz-style-row, .cgaz-zone-row, .row, .settings-card, .profile-row, label",
      ) ?? target;
    container.scrollIntoView({ block: "center", behavior: "smooth" });
    container.classList.add("setting-target");
    window.setTimeout(() => container.classList.remove("setting-target"), 1800);
  }

  /** Re-apply registry visibility/resets after fresh defaults arrive. */
  refreshSettingsAccess(): void {
    this.installSettingResets();
    this.refreshRoleVisibility();
  }

  /** The Admin page (and its Settings-defaults link) is admin-only. */
  private refreshRoleVisibility(): void {
    const adminTab = this.menuEl.querySelector<HTMLElement>('[data-page="admin"]');
    const isAdmin = canRole(this.cb.getRole(), "admin");
    adminTab?.classList.toggle("hidden", !isAdmin);
    if (!isAdmin && document.getElementById("page-admin")?.classList.contains("active")) {
      this.showPage("settings");
    }
  }

  applyAll() {
    this.fovSlider.value = String(this.settings.fov);
    this.fovInput.value = String(this.settings.fov);
    this.fovValue.textContent = `${this.settings.fov}°`;
    this.cb.onFov(this.settings.fov);

    this.cb.onSensitivity(this.settings.sensitivity);
    this.xhairEditor.setStore(this.settings.crosshair);
    // The base sensitivity is owned by this Controls card; strip any legacy
    // shared-default value before handing the store to the resolver.
    if (this.settings.weaponSettings.defaults.sensitivity !== undefined) {
      delete this.settings.weaponSettings.defaults.sensitivity;
    }
    this.weaponPanel.set(this.settings.weaponSettings, this.weaponGlobals(), this.slotRefs());
    this.cb.onCrosshair(this.settings.crosshair);
    this.cb.onWeaponSettings(this.settings.weaponSettings);

    this.hudSpectatePlayer.checked = this.settings.hudLayout.spectateUsePlayer;
    this.hudEditor.setStore(this.settings.hudLayout);
    this.cb.onHudLayout(this.settings.hudLayout);

    this.textureModeSelect.value = this.settings.textureMode;
    this.cb.onTextureMode(this.settings.textureMode);
    this.setPanini(this.settings.panini, false);

    this.cgazStylePanel.refresh();
    this.cb.onHud(this.hudSnapshot());

    const volume = Math.min(1, Math.max(0, this.settings.volume ?? 0.8));
    this.volumeSlider.value = String(volume);
    this.volumeValue.textContent = `${Math.round(volume * 100)}%`;
    this.cb.onVolume(volume);

    const tl = Math.min(1, Math.max(0, this.settings.cgTrueLightning ?? 1));
    this.trueLightningSlider.value = String(tl);
    this.trueLightningValue.textContent = tl.toFixed(2);
    this.cb.onTrueLightning(tl);

    // Player trail (persisted): seed the live renderer + all controls.
    const trailMode = (v: unknown, fb: TrailColorMode): TrailColorMode =>
      v === "random" || v === "team" ? v : v === "custom" ? v : fb;
    const trailCfg: TrailConfig = {
      seconds: Math.min(3, Math.max(0, this.settings.trailSeconds ?? 1.5)),
      width: Math.min(8, Math.max(0.5, this.settings.trailWidth ?? 3.2)),
      intensity: Math.min(2, Math.max(0.1, this.settings.trailIntensity ?? 0.6)),
      selfMode: trailMode(this.settings.trailSelfMode, "custom"),
      selfColor: this.settings.trailSelfColor ?? "#ff6a1a",
      selfTeamColor: this.settings.trailSelfTeamColor ?? "#22d3ee",
      enemyMode: trailMode(this.settings.trailEnemyMode, "team"),
      enemyColor: this.settings.trailEnemyColor ?? "#ff3b30",
      enemyTeamColor: this.settings.trailEnemyTeamColor ?? "#ff3b30",
    };
    this.syncTrailUi(trailCfg);
    this.cb.onTrail(trailCfg);
    this.syncFeedback();
    this.cb.onFeedback?.();

    // Downstream snapshot rate: keep the pickers in sync with the live value.
    this.syncDownstream(Number(this.cb.getDebug().downstream ?? this.settings.downstreamHz));

    this.cb.onBinds({ ...this.settings.binds });
    this.renderBinds();

    // Sound assignments: ensure the object exists and push to the audio system.
    if (!this.settings.sounds || typeof this.settings.sounds !== "object") {
      this.settings.sounds = {};
    }
    this.cb.onSounds({ ...this.settings.sounds });
    if (this.soundLoaded) this.renderSounds();

    this.refreshNickname();
    this.refreshProfiles();
    this.refreshPreset();
    this.renderRules();
    this.refreshModePreset();
    this.refreshModeProfiles();
    this.refreshModeSync();
    this.cb.onGameModeLocal({ ...this.settings.mode });

    // Reset buttons + per-role visibility for the settings registry.
    this.installSettingResets();
    this.refreshRoleVisibility();
    this.settingsSearch.refresh();
  }

  /** Open/close the menu. */
  setOpen(open: boolean) {
    this.menuEl.classList.toggle("open", open);
    if (open) {
      // Re-run the active page's entry hook. The markup already marks a page
      // active, so without this the maps catalog never loads and the sub-nav
      // stays hidden until you switch tabs and come back.
      const active = this.menuEl.querySelector<HTMLElement>(".page.active");
      const pageId = active?.id.replace(/^page-/, "") ?? "maps";
      this.showPage(pageId);
    }
    if (!open) this.xhairEditor.deactivate();
    else if (document.getElementById("page-hud")?.classList.contains("active")) {
      this.xhairEditor.activate();
    }
    if (open) {
      this.refreshRecall();
      this.refreshSlots();
      this.refreshNickname();
      this.refreshDebug();
    }
    this.onToggle(open);
  }

  /** Prefill the leaderboard map input with the given map. */
  prefillLeaderboardMap(map: string) {
    if (this.lbMapInput.value.trim() === "") {
      this.lbMapInput.value = map.toLowerCase();
    }
    this.updateLbFav();
  }

  isOpen(): boolean {
    return this.menuEl.classList.contains("open");
  }

  /** Shared by the menu search and the command palette (one behaviour). */
  private settingsSearchCallbacks() {
    return {
      getRole: () => this.cb.getRole(),
      getSettings: () => this.settings,
      findControls: (id: string) => this.settingControlsById(id),
      commit: () => this.store.commit(true),
      getServerFamily: () => this.cb.getServerFamily(),
      mapSuggestions: (q: string) => this.mapSuggestions(q),
      ensureMapSuggestions: () => this.ensureMapSuggestions(),
      openGroup: (id: string) => {
        const fromPalette = this.paletteOpen;
        this.closePalette();
        const page = GROUP_PAGES[id];
        if (page) {
          if (fromPalette) this.setOpen(true);
          this.showPage(page);
        }
      },
      navigate: (def: SettingDef) => {
        const fromPalette = this.paletteOpen;
        this.closePalette();
        if (fromPalette) this.setOpen(true);
        this.navigateToSetting(def);
      },
      onCommand: (line: string) => {
        this.closePalette();
        this.cb.onConsoleCommand(line);
      },
    };
  }

  isPaletteOpen(): boolean {
    return this.paletteOpen;
  }

  /** Open the in-game palette; releases pointer lock so the cursor is usable. */
  openPalette(): void {
    if (this.paletteOpen || this.isOpen()) return;
    this.paletteOpen = true;
    this.paletteEl.classList.remove("hidden");
    this.cb.onPaletteToggle(true);
    this.paletteInput.value = "";
    this.paletteSearch.close();
    // Focus on the next frame: focusing during the opening keydown lets the
    // browser insert that key's character into the fresh input (pressing M
    // used to start the query with "m").
    requestAnimationFrame(() => {
      if (this.paletteOpen) this.paletteInput.focus();
    });
    document.exitPointerLock?.();
  }

  closePalette(): void {
    if (!this.paletteOpen) return;
    this.paletteOpen = false;
    this.cb.onPaletteToggle(false);
    this.paletteSearch.close();
    this.paletteInput.value = "";
    this.paletteEl.classList.add("hidden");
    this.paletteInput.blur();
  }

  togglePalette(): void {
    if (this.paletteOpen) this.closePalette();
    else this.openPalette();
  }

  /** Load and render the leaderboard for a map (formatted table). */
  async refreshLeaderboard(map: string) {
    this.lbMap = map.toLowerCase();
    if (this.lbMapInput.value !== this.lbMap) this.lbMapInput.value = this.lbMap;
    this.updateLbFav();
    try {
      const entries = await fetchLeaderboard(this.lbMap);
      if (entries.length === 0) {
        this.leaderboardEl.innerHTML = `<div class="lb-empty">no times yet</div>`;
        return;
      }
      const fmt = (ms: number) => {
        const m = Math.floor(ms / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        const cs = Math.floor((ms % 1000) / 10);
        return `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
      };
      const me = getNickname();
      const rows = entries.map((e, i) => {
        const isMe = me !== null && e.nickname === me;
        const wr = i === 0 ? ' <span class="badge badge-warning badge-xs">WR</span>' : "";
        const pj = e.prejumped
          ? ' <span class="badge badge-error badge-xs" title="prejumped before the start line">PJ</span>'
          : "";
        return `<tr class="${isMe ? "lb-me" : ""}">
          <td class="lb-pos">${i + 1}</td>
          <td>${q3Html(e.nickname)}${isMe ? ' <span style="opacity:.6">(you)</span>' : ""}${wr}${pj}</td>
          <td class="lb-time">${fmt(e.time_ms)}</td>
        </tr>`;
      });
      this.leaderboardEl.innerHTML =
        `<table class="table table-sm table-zebra"><thead><tr><th>#</th><th>player</th><th class="lb-time">time</th></tr></thead><tbody>${rows.join("")}</tbody></table>`;
    } catch {
      this.leaderboardEl.innerHTML = `<div class="lb-empty">leaderboard unavailable</div>`;
    }
  }

  /** Render the connected-players / session scoreboard. */
  async renderPlayers() {
    if (!this.playersEl) return;
    // Don't rebuild the table while the player is using a team dropdown; the
    // 2 s poll would otherwise close/reset the select mid-click.
    if (
      document.activeElement instanceof HTMLSelectElement &&
      document.activeElement.classList.contains("pl-team-select")
    ) {
      return;
    }
    const roster = this.cb.getRoster();
    const map = this.cb.getCurrentMap();
    void this.renderProfile();
    // Cache the leaderboard per map (personal bests live there).
    if (map && this.playersLbMap !== map) {
      try {
        this.playersLb = await fetchLeaderboard(map);
        this.playersLbMap = map;
      } catch {
        this.playersLb = [];
      }
    }
    const fmt = (ms: number) => {
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      const cs = Math.floor((ms % 1000) / 10);
      return `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
    };
    if (roster.length === 0) {
      this.playersEl.innerHTML = `<div class="pl-row" style="justify-content:center;color:var(--ui-text-faint)">not connected — press Play online</div>`;
      return;
    }
    const wrTime = this.playersLb[0]?.time_ms;
    const pbByNick = new Map(this.playersLb.map((e) => [e.nickname, e.time_ms]));
    const me = getNickname();
    const sorted = [...roster].sort((a, b) => {
      const av = a.best > 0 ? a.best : Number.POSITIVE_INFINITY;
      const bv = b.best > 0 ? b.best : Number.POSITIVE_INFINITY;
      return av - bv;
    });
    const head = `<div class="pl-head"><span>player</span><span>team</span><span class="pl-time">ping</span><span class="pl-time">session</span><span class="pl-time">pb</span></div>`;
    const team = (v: number | undefined) => (v === 1 ? 1 : v === 2 ? 2 : 0);
    const rows = sorted.map((r) => {
      const isMe = me !== null && r.nick === me;
      const pb = pbByNick.get(r.nick);
      const crown =
        pb !== undefined && wrTime !== undefined && pb === wrTime
          ? ' <span class="pl-crown" title="world record">♛</span>'
          : "";
      const name = r.nick ? q3Html(r.nick) : `player ${r.slot}`;
      const t = team(r.team);
      return `<div class="pl-row${isMe ? " me" : ""}">
        <span>${name}${crown}${isMe ? ' <span style="opacity:.6">(you)</span>' : ""}</span>
        <select class="pl-team-select" data-slot="${r.slot}" title="playtest: move this player">
          <option value="0"${t === 0 ? " selected" : ""}>Spec</option>
          <option value="1"${t === 1 ? " selected" : ""}>Red</option>
          <option value="2"${t === 2 ? " selected" : ""}>Blue</option>
        </select>
        <span class="pl-time">${r.ping ? r.ping + " ms" : "—"}</span>
        <span class="pl-time">${r.best > 0 ? fmt(r.best) : "—"}</span>
        <span class="pl-time">${pb !== undefined ? fmt(pb) : "—"}</span>
      </div>`;
    });
    this.playersEl.innerHTML = head + rows.join("");
    this.playersEl.querySelectorAll<HTMLSelectElement>(".pl-team-select").forEach((sel) => {
      sel.addEventListener("change", () => {
        const slot = Number(sel.dataset.slot);
        const t = Number(sel.value);
        if (Number.isInteger(slot) && (t === 0 || t === 1 || t === 2)) this.cb.onTeam(slot, t);
      });
    });
  }

  /** Personal ratings + recent match history (Phase 5 profile UI). */
  private async renderProfile(): Promise<void> {
    const identity = getIdentity();
    if (!this.profileRatingsEl || !this.profileMatchesEl) return;
    if (!identity) {
      this.profileRatingsEl.innerHTML =
        '<div class="pl-row" style="justify-content:center;color:var(--ui-text-faint)">set a nickname to get rated</div>';
      this.profileMatchesEl.innerHTML = "";
      return;
    }
    const now = Date.now();
    if (this.profileToken === identity.token && now - this.profileLoadedAt < 15000) return;
    const profile = await fetchProfile(identity.token);
    if (!profile) {
      this.profileRatingsEl.innerHTML =
        '<div class="pl-row" style="justify-content:center;color:var(--ui-text-faint)">ratings unavailable</div>';
      return;
    }
    this.profileToken = identity.token;
    this.profileLoadedAt = now;
    const ratings = profile.ratings ?? [];
    if (ratings.length === 0) {
      this.profileRatingsEl.innerHTML =
        '<div class="pl-row" style="justify-content:center;color:var(--ui-text-faint)">no rated matches yet</div>';
    } else {
      const head = `<div class="pl-head"><span>mode</span><span class="pl-time">rating</span><span class="pl-time">rd</span><span class="pl-time">games</span></div>`;
      const rows = ratings.map(
        (r) => `<div class="pl-row"><span>${q3Html(r.mode)}</span><span class="pl-time">${Math.round(r.rating)}</span><span class="pl-time">±${Math.round(r.rd)}</span><span class="pl-time">${r.games}</span></div>`,
      );
      this.profileRatingsEl.innerHTML = head + rows.join("");
    }
    const matches = profile.matches ?? [];
    if (matches.length === 0) {
      this.profileMatchesEl.innerHTML =
        '<div class="pl-row" style="justify-content:center;color:var(--ui-text-faint)">no matches yet</div>';
      return;
    }
    const head = `<div class="pl-head"><span>map · mode</span><span class="pl-time">result</span><span class="pl-time">rating</span><span class="pl-time">when</span></div>`;
    const rows = matches.map((m) => {
      const delta =
        m.rating_after !== null && m.rating_before !== null
          ? `${m.rating_before.toFixed(0)} → ${m.rating_after.toFixed(0)}`
          : "—";
      const result = m.place > 0 ? `#${m.place}` : m.team > 0 ? `team ${m.team}` : "—";
      const when = new Date(m.created_at).toLocaleString();
      return `<div class="pl-row"><span>${q3Html(m.map)} · ${q3Html(m.mode)}</span><span class="pl-time">${result}</span><span class="pl-time">${delta}</span><span class="pl-time">${q3Html(when)}</span></div>`;
    });
    this.profileMatchesEl.innerHTML = head + rows.join("");
  }

  /**
   * Sync + persist binds changed outside the menu (the console `bind`/`unbind`
   * commands). Keeping `this.settings` in sync matters: a later save from any
   * other settings control must not write the old bind map back.
   */
  applyExternalBinds(binds: BindMap) {
    this.settings.binds = { ...binds };
    this.save();
    this.renderBinds();
  }

  /** Render both the Settings and Practice bind lists with editable entries. */
  private renderBinds() {
    this.renderBindGroups(this.bindsEl, ACTION_GROUPS);
    if (this.practiceBindsEl) {
      this.practiceBindsEl.innerHTML = "";
      this.practiceBindsEl.classList.add("binds-cards");
      this.practiceBindsEl.append(this.bindCard("Practice & recall", PRACTICE_ACTIONS));
      refreshComponentStyles();
    }
    this.renderLandingBinds();
    // Keep the inline Rules-tab weapon bind buttons in sync too.
    for (const [a, b] of this.ruleBindButtons) this.updateBindButton(b, a);
  }

  /** Mount the landing page's compact editable "Your controls" panel. */
  mountLandingBinds(container: HTMLElement) {
    this.landingBindsEl = container;
    this.renderLandingBinds();
  }

  /** Rebuild the two-column essential-bind editor on the landing page. */
  private renderLandingBinds() {
    if (!this.landingBindsEl) return;
    this.landingBindsEl.innerHTML = "";
    for (const group of ESSENTIAL_ACTION_GROUPS) {
      const col = document.createElement("div");
      const title = document.createElement("div");
      title.className = "bind-group-title";
      title.textContent = group.title;
      const list = document.createElement("div");
      list.className = "bind-group-list";
      col.append(title, list);
      this.landingBindsEl.append(col);
      this.renderBindList(
        list,
        group.ids.map((id) => ({ id, label: ACTION_LABELS.get(id) ?? id })),
      );
    }
  }

  /** Set one bind button's label from the current settings. */
  private updateBindButton(btn: HTMLButtonElement, action: string) {
    const codes: string[] = [];
    for (const [c, act] of Object.entries(this.settings.binds)) {
      if (act === action) codes.push(c);
    }
    btn.textContent =
      this.captureAction === action
        ? "press a key… (Backspace clears)"
        : codes.length
          ? codes.map(displayCode).join(" / ")
          : "—";
  }

  /** Render grouped keybinds (Settings tab): one design-system card per category. */
  private renderBindGroups(el: HTMLElement, groups: { title: string; ids: string[] }[]) {
    if (!el) return;
    el.innerHTML = "";
    el.classList.add("binds-cards");
    for (const g of groups) {
      el.append(
        this.bindCard(
          g.title,
          g.ids.map((id) => ({ id, label: ACTION_LABELS.get(id) ?? id })),
        ),
      );
    }
    // Cards are built after the theme was applied, so re-run the class pass.
    refreshComponentStyles();
  }

  /**
   * One category of binds rendered as a design-system card (`data-ds="card"`),
   * so /design → Components → Card restyles them (surface, padding, border,
   * background, …). `data-ds-keep` keeps the layout class through the rewrite.
   */
  private bindCard(title: string, actions: { id: string; label: string }[]): HTMLElement {
    const card = document.createElement("section");
    card.className = "binds-card";
    card.dataset.ds = "card";
    card.dataset.dsKeep = "binds-card";

    const body = document.createElement("div");
    body.className = "card-body";

    const heading = document.createElement("h3");
    heading.className = "card-title";
    heading.textContent = title;

    const list = document.createElement("div");
    list.className = "bind-group-list";

    body.append(heading, list);
    card.append(body);
    this.renderBindList(list, actions);
    return card;
  }

  private renderBindList(el: HTMLElement, actions: { id: string; label: string }[]) {
    if (!el) return;
    el.innerHTML = "";
    for (const a of actions) {
      // Show *every* code bound to this action (an action can have several,
      // e.g. a wheel direction and a key).
      const codes: string[] = [];
      for (const [c, act] of Object.entries(this.settings.binds)) {
        if (act === a.id) codes.push(c);
      }
      const row = document.createElement("div");
      row.className = "bind-row";
      const label = document.createElement("span");
      label.className = "bind-label";
      label.textContent = a.label;
      const btn = document.createElement("button");
      btn.className = "bind-key";
      btn.dataset.action = a.id;
      btn.textContent =
        this.captureAction === a.id
          ? "press a key… (Backspace clears)"
          : codes.length
            ? codes.map(displayCode).join(" / ")
            : "—";
      btn.title = "click to bind · right-click (or Backspace) to clear";
      btn.addEventListener("click", () => this.startCapture(a.id));
      // Right-click clears the bind (unbind).
      btn.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        e.stopPropagation();
        // A right-click that was just used to set this bind must not clear it
        // again (the contextmenu fires right after the bind's mousedown).
        if (this.captureAction === a.id || performance.now() - this.mouseBindAt < 1200) return;
        this.clearBind(a.id);
        this.renderBinds();
      });
      row.append(label, btn);
      el.append(row);
    }
  }

  /** Begin capturing a new key/mouse bind for an action. */
  private startCapture(action: string) {
    this.captureAction = action;
    this.renderBinds();

    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const code = e.code;
      if (code === "Escape") {
        this.captureAction = null;
      } else if (code === "Backspace" || code === "Delete") {
        // Clear the bind (unbind).
        this.clearBind(action);
      } else {
        this.setBind(action, code);
      }
      cleanup();
      this.renderBinds();
    };
    const onMouse = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.mouseBindAt = performance.now();
      // The contextmenu for this click lands after mouseup; consume it so the
      // button's "right-click clears" handler cannot undo the bind we just set.
      if (e.button === 2) {
        this.swallowNextContextMenu = true;
        window.setTimeout(() => {
          this.swallowNextContextMenu = false;
        }, 1200);
      }
      const code = "Mouse" + e.button;
      this.setBind(action, code);
      cleanup();
      this.renderBinds();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const code = e.deltaY < 0 ? "WheelUp" : "WheelDown";
      this.setBind(action, code);
      cleanup();
      this.renderBinds();
    };
    const cleanup = () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("mousedown", onMouse, true);
      window.removeEventListener("wheel", onWheel, true);
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("mousedown", onMouse, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
  }

  /** Set a bind, removing any conflicting binding for the same code. */
  private setBind(action: string, code: string) {
    // Remove any existing binding for this code.
    for (const c of Object.keys(this.settings.binds)) {
      if (c === code) delete this.settings.binds[c];
    }
    // Remove any existing binding for this action.
    for (const [c] of Object.entries(this.settings.binds)) {
      const a = this.settings.binds[c];
      if (a === action) delete this.settings.binds[c];
    }
    this.settings.binds[code] = action;
    this.captureAction = null;
    this.save();
    this.cb.onBinds({ ...this.settings.binds });
  }

  /** Remove every code bound to an action (unbind). */
  private clearBind(action: string) {
    for (const [c, a] of Object.entries(this.settings.binds)) {
      if (a === action) delete this.settings.binds[c];
    }
    this.captureAction = null;
    this.save();
    this.cb.onBinds({ ...this.settings.binds });
  }
}
